import React, { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Field, Input, SearchBox, Select, Spinner, Switch, Tab, TabList, Textarea } from "@fluentui/react-components";
import {
  Add20Regular,
  ArrowDown16Regular,
  ArrowDownload20Regular,
  ArrowSync20Regular,
  ArrowUp16Regular,
  ChevronLeft20Regular,
  ChevronRight20Regular,
  Copy20Regular,
  DataBarVertical20Regular,
  Delete20Regular,
  Edit20Regular,
  LocalLanguage20Regular,
  Open20Regular,
  People20Regular,
  PhoneLaptop20Regular,
  PlugConnected20Regular,
  ShieldKeyhole20Regular,
} from "@fluentui/react-icons";
import styles from "./TaskManager.module.css";
import tp from "./Taskpages.module.css";
import ad from "./Taskadmin.module.css";
import { ApiError, R, TASKS_API, api, camelize, crud, iso, msgOf, readToken, utc } from "./Api/Taskapi";
import type { Paged } from "./Api/Taskapi";
import { Chip, FormDialog, Head, Page, Tile, Wait, asList, downloadCsv, fmtDate, fmtStamp, nice, useAct, useLoad } from "./Taskpages";
import type { Notify, PageCtx } from "./Taskpages";

/* ------------------------------------------------------------------ *
 *  Admin-side pages of the Task Manager: dashboards and exports,
 *  people and sharing, access and audit, languages and currencies,
 *  devices and offline sync, connectors. Everything here talks to the
 *  same API as the rest of the app (taskApi.ts).
 * ------------------------------------------------------------------ */

/* ===================== shared helpers ===================== */
type Row = { id: number } & Record<string, unknown>;
type Query = Record<string, string | number | boolean | undefined>;

const isAdminOf = (ctx: PageCtx) => ctx.me.workspaces.find((w:any) => w.id === ctx.workspaceId)?.role === "ADMIN";
const str = (v: unknown) => (v === null || v === undefined ? "" : String(v));
const num = (v: unknown) => (typeof v === "number" ? v : Number(v));
const ROLES: [string, string][] = [
  ["ADMIN", "Admin"],
  ["MEMBER", "Member"],
  ["GUEST", "Guest"],
  ["VIEWER", "Viewer"],
];

/** GET a file with the sign-in token and save it. */
async function download(path: string, query: Query, fallback: string): Promise<void> {
  const token = readToken();
  const p = Object.entries(query)
    .filter(([, v]) => v !== undefined && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join("&");
  let res: Response;
  try {
    res = await fetch(`${TASKS_API}${path}${p ? `?${p}` : ""}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  } catch {
    throw new ApiError(0, "Couldn’t reach the Task Manager. Check your connection.");
  }
  if (!res.ok) {
    let msg = "";
    try {
      const j = (await res.json()) as Record<string, unknown>;
      if (typeof j.title === "string") msg = j.title;
    } catch {
      /* no readable body */
    }
    throw new ApiError(res.status, msg || (res.status === 403 ? "You don’t have permission to do that." : `The download failed (${res.status}).`));
  }
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") ?? "";
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = m?.[1] ? decodeURIComponent(m[1]) : fallback;
  a.click();
  URL.revokeObjectURL(url);
}

const copyText = (text: string, notify: Notify) => {
  void navigator.clipboard
    .writeText(text)
    .then(() => notify("success", "Copied."))
    .catch(() => notify("info", "Select the text and copy it."));
};

const Tabs: React.FC<{ value: string; onChange: (v: string) => void; items: [string, string][] }> = ({ value, onChange, items }) => (
  <div className={styles.bar}>
    <TabList selectedValue={value} onTabSelect={(_, d) => onChange(String(d.value))}>
      {items.map(([k, label]) => (
        <Tab key={k} value={k}>
          {label}
        </Tab>
      ))}
    </TabList>
  </div>
);

const Pretty: React.FC<{ text?: string | null }> = ({ text }) => {
  if (!text) return <span className={styles.muted}>—</span>;
  let out = text;
  try {
    out = JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    /* show as is */
  }
  return <pre className={ad.pre}>{out}</pre>;
};

/* ===================== generic table + form for one entity ===================== */
type Kind = "text" | "number" | "bool" | "date" | "datetime" | "textarea" | "json" | "select" | "user" | "project";
interface FieldDef {
  key: string;
  label: string;
  kind?: Kind;
  options?: [string, string][];
  required?: boolean;
  hint?: string;
  placeholder?: string;
  /** show as a column (default true) */
  list?: boolean;
  /** show in the form (default true) */
  form?: boolean;
  /** only sent when creating */
  createOnly?: boolean;
}

interface TableProps {
  ctx: PageCtx;
  route: string;
  title: string;
  noun: string;
  fields: FieldDef[];
  query?: Query;
  defaults?: Record<string, unknown>;
  sort?: string;
  canWrite?: boolean;
  canCreate?: boolean;
  canDelete?: boolean;
  search?: boolean;
  emptyText?: string;
  rowActions?: (row: Row, reload: () => void) => React.ReactNode;
  toolbar?: React.ReactNode;
}

const PAGE = 25;

const EntityTable: React.FC<TableProps> = ({ ctx, route, title, noun, fields, query, defaults, sort, canWrite = true, canCreate = true, canDelete = true, search, emptyText, rowActions, toolbar }) => {
  const act = useAct(ctx.notify);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState<{ mode: "create" | "edit"; row: Row | null } | null>(null);
  const [vals, setVals] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [sure, setSure] = useState<number | null>(null);
  const qKey = JSON.stringify(query ?? {});
  const l = useLoad(
    () => crud.list<Row>(route, { ...(query ?? {}), ...(sort ? { sort } : {}), ...(q.trim() ? { q: q.trim() } : {}), page, pageSize: PAGE }),
    [route, qKey, sort, q, page],
  );
  const items = (l.data as Paged<Row> | null)?.items ?? [];
  const shownFields = fields.filter((f) => f.list !== false);
  const personName = (id: unknown) => ctx.people.find((p) => p.id === Number(id))?.name ?? (id ? `User ${str(id)}` : "—");
  const projectName = (id: unknown) => ctx.projects.find((p) => p.id === Number(id))?.name ?? (id ? `Project ${str(id)}` : "—");

  const cell = (f: FieldDef, r: Row): React.ReactNode => {
    const v = r[f.key];
    switch (f.kind) {
      case "bool":
        return v ? <Chip text="Yes" /> : <span className={styles.muted}>No</span>;
      case "date":
        return fmtDate(v ? str(v) : null);
      case "datetime":
        return fmtStamp(v ? str(v) : null);
      case "user":
        return personName(v);
      case "project":
        return projectName(v);
      case "select":
        return f.options?.find(([k]) => k === str(v))?.[1] ?? (str(v) || "—");
      case "json":
      case "textarea":
        return <span className={ad.clip}>{str(v) || "—"}</span>;
      default:
        return str(v) === "" ? "—" : str(v);
    }
  };

  const open = (mode: "create" | "edit", row: Row | null) => {
    const v: Record<string, string> = {};
    for (const f of fields) {
      const cur = row ? row[f.key] : undefined;
      v[f.key] = f.kind === "bool" ? String(cur === undefined ? false : !!cur) : f.kind === "date" && cur ? str(cur).slice(0, 10) : f.kind === "datetime" && cur ? str(cur).slice(0, 16) : str(cur);
    }
    setVals(v);
    setDlg({ mode, row });
  };

  const submit = async () => {
    if (!dlg) return;
    const body: Record<string, unknown> = dlg.mode === "create" ? { ...(defaults ?? {}) } : {};
    for (const f of fields) {
      if (f.form === false || (dlg.mode === "edit" && f.createOnly)) continue;
      const raw = vals[f.key] ?? "";
      if (f.required && raw.trim() === "" && f.kind !== "bool") {
        ctx.notify("error", `${f.label} is required.`);
        return;
      }
      switch (f.kind) {
        case "number":
          body[f.key] = raw.trim() === "" ? null : Number(raw);
          break;
        case "bool":
          body[f.key] = raw === "true";
          break;
        case "user":
        case "project":
          body[f.key] = raw === "" ? null : Number(raw);
          break;
        case "datetime":
          body[f.key] = raw === "" ? null : new Date(raw).toISOString();
          break;
        case "json":
          if (raw.trim() !== "") {
            try {
              JSON.parse(raw);
            } catch {
              ctx.notify("error", `${f.label} must be valid JSON.`);
              return;
            }
          }
          body[f.key] = raw.trim() === "" ? null : raw;
          break;
        default:
          body[f.key] = raw === "" ? null : raw;
      }
    }
    setBusy(true);
    const ok = await act(() => (dlg.mode === "create" ? crud.create(route, body) : crud.update(route, dlg.row?.id ?? 0, body)), dlg.mode === "create" ? `${nice(noun)} added.` : "Saved.");
    setBusy(false);
    if (ok) {
      setDlg(null);
      l.reload();
    }
  };

  const input = (f: FieldDef) => {
    const v = vals[f.key] ?? "";
    const set = (x: string) => setVals((s) => ({ ...s, [f.key]: x }));
    switch (f.kind) {
      case "bool":
        return <Switch checked={v === "true"} label={v === "true" ? "Yes" : "No"} onChange={(_, d) => set(String(d.checked))} />;
      case "number":
        return <Input type="number" value={v} placeholder={f.placeholder ?? ""} onChange={(_, d) => set(d.value)} />;
      case "date":
        return <Input type="date" value={v} onChange={(_, d) => set(d.value)} />;
      case "datetime":
        return <Input type="datetime-local" value={v} onChange={(_, d) => set(d.value)} />;
      case "textarea":
      case "json":
        return <Textarea value={v} rows={f.kind === "json" ? 4 : 3} resize="vertical" placeholder={f.placeholder ?? ""} className={f.kind === "json" ? ad.mono : ""} onChange={(_, d) => set(d.value)} />;
      case "select":
        return (
          <Select value={v} onChange={(_, d) => set(d.value)}>
            {!f.required && <option value="">—</option>}
            {(f.options ?? []).map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </Select>
        );
      case "user":
        return (
          <Select value={v} onChange={(_, d) => set(d.value)}>
            <option value="">—</option>
            {ctx.people.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.name}
              </option>
            ))}
          </Select>
        );
      case "project":
        return (
          <Select value={v} onChange={(_, d) => set(d.value)}>
            <option value="">—</option>
            {ctx.projects.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.name}
              </option>
            ))}
          </Select>
        );
      default:
        return <Input value={v} placeholder={f.placeholder ?? ""} onChange={(_, d) => set(d.value)} />;
    }
  };

  const reload = l.reload;
  return (
    <div className={tp.card}>
      <div className={tp.rowBetween}>
        <h3 className={tp.cardTitle}>{title}</h3>
        <div className={tp.actions}>
          {search && <SearchBox className={styles.search} placeholder="Search" value={q} onChange={(_, d) => (setQ(d.value), setPage(1))} />}
          {toolbar}
          {canCreate && canWrite && (
            <Button className={styles.primary} appearance="primary" icon={<Add20Regular />} onClick={() => open("create", null)}>
              Add {noun}
            </Button>
          )}
        </div>
      </div>
      <Wait loading={l.loading && !l.data} error={l.error} empty={!l.loading && items.length === 0 && (emptyText ?? `No ${noun}s yet`)} onRetry={l.reload} />
      {items.length > 0 && (
        <div className={tp.scroll}>
          <table className={tp.tbl}>
            <thead>
              <tr>
                {shownFields.map((f) => (
                  <th key={f.key}>{f.label}</th>
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id}>
                  {shownFields.map((f, i) => (
                    <td key={f.key} className={i === 0 ? tp.strong : undefined}>
                      {cell(f, r)}
                    </td>
                  ))}
                  <td className={tp.actions}>
                    {rowActions?.(r, reload)}
                    {canWrite && (
                      <Button size="small" appearance="subtle" icon={<Edit20Regular />} aria-label={`Edit ${noun}`} onClick={() => open("edit", r)} />
                    )}
                    {canWrite && canDelete &&
                      (sure === r.id ? (
                        <>
                          <Button
                            size="small"
                            onClick={async () => {
                              setSure(null);
                              if (await act(() => crud.remove(route, r.id), `${nice(noun)} deleted.`)) reload();
                            }}
                          >
                            Delete
                          </Button>
                          <Button size="small" appearance="subtle" onClick={() => setSure(null)}>
                            Keep
                          </Button>
                        </>
                      ) : (
                        <Button size="small" appearance="subtle" icon={<Delete20Regular />} aria-label={`Delete ${noun}`} onClick={() => setSure(r.id)} />
                      ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(page > 1 || items.length === PAGE) && (
        <div className={ad.pager}>
          <Button size="small" icon={<ChevronLeft20Regular />} disabled={page <= 1} onClick={() => setPage((p) => p - 1)} aria-label="Previous page" />
          <span className={styles.muted}>Page {page}</span>
          <Button size="small" icon={<ChevronRight20Regular />} disabled={items.length < PAGE} onClick={() => setPage((p) => p + 1)} aria-label="Next page" />
        </div>
      )}
      <FormDialog open={dlg !== null} title={dlg?.mode === "create" ? `Add ${noun}` : `Edit ${noun}`} action={dlg?.mode === "create" ? "Add" : "Save"} busy={busy} onClose={() => setDlg(null)} onSubmit={() => void submit()}>
        {fields
          .filter((f) => f.form !== false && !(dlg?.mode === "edit" && f.createOnly))
          .map((f) => (
            <Field key={f.key} label={f.label} required={!!f.required} hint={f.hint ?? null}>
              {input(f)}
            </Field>
          ))}
      </FormDialog>
    </div>
  );
};

/* ===================== charts (inline SVG) ===================== */
const PALETTE = ["#0f6cbd", "#2ea043", "#f2994a", "#8764b8", "#038387", "#e5484d", "#986f0b", "#6b7280"];
const fmtNum = (n: number) => (Number.isInteger(n) ? n.toLocaleString() : n.toLocaleString(undefined, { maximumFractionDigits: 2 }));
const toNum = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v)) ? Number(v) : null);
const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

interface Series {
  labels: string[];
  values: number[];
  name: string;
}
/** the last all-number column is the value, the first other column is the label */
const toSeries = (columns: string[], rows: unknown[][]): Series => {
  let vi = -1;
  for (let c = columns.length - 1; c >= 0; c--) {
    if (rows.length > 0 && rows.every((r) => r[c] === null || r[c] === undefined || toNum(r[c]) !== null) && rows.some((r) => toNum(r[c]) !== null)) {
      vi = c;
      break;
    }
  }
  const li = columns.findIndex((_, i) => i !== vi);
  return {
    labels: rows.map((r, i) => (li >= 0 ? str(r[li]) || `#${i + 1}` : `#${i + 1}`)),
    values: rows.map((r) => (vi >= 0 ? (toNum(r[vi]) ?? 0) : 0)),
    name: vi >= 0 ? (columns[vi] ?? "") : "",
  };
};

const BarChart: React.FC<{ s: Series }> = ({ s }) => {
  const W = 520;
  const H = 220;
  const P = 34;
  const max = Math.max(1, ...s.values);
  const bw = (W - P * 2) / Math.max(1, s.values.length);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={ad.chart} role="img" aria-label={`Bar chart of ${s.name || "values"}`}>
      <line x1={P} y1={H - P} x2={W - P} y2={H - P} stroke="#d1d1d1" />
      <text x={P - 4} y={P - 14} fontSize="11" fill="#616161" textAnchor="end">
        {fmtNum(max)}
      </text>
      {s.values.map((v, i) => {
        const h = (v / max) * (H - P * 2);
        return (
          <g key={i}>
            <rect x={P + i * bw + bw * 0.15} y={H - P - h} width={bw * 0.7} height={Math.max(h, 1)} rx={3} fill={PALETTE[i % PALETTE.length] ?? "#0f6cbd"}>
              <title>{`${s.labels[i] ?? ""}: ${fmtNum(v)}`}</title>
            </rect>
            {s.values.length <= 14 && (
              <text x={P + i * bw + bw / 2} y={H - P + 14} fontSize="10" fill="#616161" textAnchor="middle">
                {clip(s.labels[i] ?? "", 9)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
};

const LineChart: React.FC<{ s: Series }> = ({ s }) => {
  const W = 520;
  const H = 220;
  const P = 34;
  if (s.values.length < 2) return <BarChart s={s} />;
  const max = Math.max(1, ...s.values);
  const x = (i: number) => P + (i / (s.values.length - 1)) * (W - P * 2);
  const y = (v: number) => H - P - (v / max) * (H - P * 2);
  const d = s.values.map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={ad.chart} role="img" aria-label={`Line chart of ${s.name || "values"}`}>
      <line x1={P} y1={H - P} x2={W - P} y2={H - P} stroke="#d1d1d1" />
      <text x={P - 4} y={P - 14} fontSize="11" fill="#616161" textAnchor="end">
        {fmtNum(max)}
      </text>
      <path d={d} fill="none" stroke="#0f6cbd" strokeWidth="2.5" />
      {s.values.map((v, i) => (
        <circle key={i} cx={x(i)} cy={y(v)} r={3.5} fill="#0f6cbd">
          <title>{`${s.labels[i] ?? ""}: ${fmtNum(v)}`}</title>
        </circle>
      ))}
      <text x={P} y={H - 8} fontSize="10" fill="#616161">
        {clip(s.labels[0] ?? "", 14)}
      </text>
      <text x={W - P} y={H - 8} fontSize="10" fill="#616161" textAnchor="end">
        {clip(s.labels[s.labels.length - 1] ?? "", 14)}
      </text>
    </svg>
  );
};

const PieChart: React.FC<{ s: Series }> = ({ s }) => {
  const total = s.values.reduce((a, b) => a + Math.max(0, b), 0);
  if (total <= 0) return <div className={styles.emptyRow}>Nothing to chart</div>;
  const R0 = 80;
  let angle = -Math.PI / 2;
  const slices = s.values.map((v, i) => {
    const a = (Math.max(0, v) / total) * Math.PI * 2;
    const x1 = 100 + R0 * Math.cos(angle);
    const y1 = 100 + R0 * Math.sin(angle);
    angle += a;
    const x2 = 100 + R0 * Math.cos(angle);
    const y2 = 100 + R0 * Math.sin(angle);
    const path = a >= Math.PI * 2 - 0.0001 ? `M100,${100 - R0} A${R0},${R0} 0 1 1 99.99,${100 - R0} Z` : `M100,100 L${x1.toFixed(2)},${y1.toFixed(2)} A${R0},${R0} 0 ${a > Math.PI ? 1 : 0} 1 ${x2.toFixed(2)},${y2.toFixed(2)} Z`;
    return { path, color: PALETTE[i % PALETTE.length] ?? "#0f6cbd", label: s.labels[i] ?? "", v };
  });
  return (
    <div className={ad.pie}>
      <svg viewBox="0 0 200 200" className={ad.pieSvg} role="img" aria-label={`Pie chart of ${s.name || "values"}`}>
        {slices.map((sl, i) => (
          <path key={i} d={sl.path} fill={sl.color} stroke="#fff" strokeWidth="1.5">
            <title>{`${sl.label}: ${fmtNum(sl.v)}`}</title>
          </path>
        ))}
      </svg>
      <ul className={ad.legend}>
        {slices.map((sl, i) => (
          <li key={i}>
            <span className={ad.swatch} style={{ background: sl.color }} />
            {clip(sl.label, 24)} · {fmtNum(sl.v)} ({Math.round((sl.v / total) * 100)}%)
          </li>
        ))}
      </ul>
    </div>
  );
};

const DataTable: React.FC<{ columns: string[]; rows: unknown[][]; max?: number }> = ({ columns, rows, max = 100 }) =>
  rows.length === 0 ? (
    <div className={styles.emptyRow}>Nothing to show</div>
  ) : (
    <div className={tp.scroll}>
      <table className={tp.tbl}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, max).map((r, i) => (
            <tr key={i}>
              {r.map((v, j) => (
                <td key={j}>{v === null || v === undefined ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > max && <div className={styles.muted}>Showing the first {max} of {rows.length} rows.</div>}
    </div>
  );

interface ReportOut {
  type: string;
  columns: string[];
  rows: unknown[][];
  count: number;
  currency?: string;
}
interface WidgetData {
  id: number;
  widgetType: string;
  title?: string | null;
  layout?: { span?: number } | null;
  data: { columns: string[]; rows: unknown[][] } | null;
  error?: string | null;
}

const WidgetView: React.FC<{ w: WidgetData }> = ({ w }) => {
  const type = w.widgetType.toUpperCase();
  const d = w.data;
  let body: React.ReactNode;
  if (w.error) body = <div className={ad.widgetError}>This widget couldn’t load ({w.error.replace("error.", "").replace(/_/g, " ")}).</div>;
  else if (!d) body = <div className={styles.emptyRow}>This widget has no report set up</div>;
  else if (type === "KPI") {
    const s = toSeries(d.columns, d.rows);
    const first = d.rows[0];
    const vi = first ? first.findIndex((v) => toNum(v) !== null) : -1;
    const value = vi >= 0 && first ? (toNum(first[vi]) ?? 0) : d.rows.length;
    body = (
      <div className={ad.kpi}>
        <div className={ad.kpiNum}>{fmtNum(value)}</div>
        <div className={ad.kpiLbl}>{vi >= 0 ? (d.columns[vi] ?? s.name) : "rows"}</div>
      </div>
    );
  } else if (type === "TABLE") body = <DataTable columns={d.columns} rows={d.rows} max={50} />;
  else {
    const s = toSeries(d.columns, d.rows);
    body = d.rows.length === 0 ? <div className={styles.emptyRow}>Nothing to show</div> : type === "PIE" ? <PieChart s={s} /> : type === "LINE" ? <LineChart s={s} /> : <BarChart s={s} />;
  }
  return (
    <section className={`${tp.card} ${w.layout?.span === 2 ? ad.span2 : ""}`} aria-label={w.title ?? nice(type)}>
      <h3 className={tp.cardTitle}>{w.title || nice(type)}</h3>
      {body}
    </section>
  );
};

/* =================================================================== *
 *  Dashboards, reports, exports
 * =================================================================== */
interface Catalog {
  reportTypes: string[];
  datasets: string[];
  formats: string[];
  widgetTypes: string[];
}
interface Dash {
  id: number;
  name: string;
  isShared: boolean;
  scopeType: string;
  projectId?: number | null;
  isDefault: boolean;
}
interface WidgetRow {
  id: number;
  widgetType: string;
  title?: string | null;
  sortOrder: number;
}
interface SavedReport {
  id: number;
  name: string;
  reportType: string;
  isShared: boolean;
}
interface Exec {
  totals: { projects: number; tasks: number; done: number; overdue: number; percentComplete: number; red: number; amber: number; green: number };
  columns: string[];
  projects: unknown[][];
}
interface Rollup {
  portfolioId: number;
  totals: Exec["totals"];
  projects: { projectId: number; name: string; status: string; endDate?: string | null; totalTasks: number; doneTasks: number; overdueTasks: number; remainingMinutes: number; percentComplete: number; health: string }[];
}
interface Feed {
  dataset: string;
  columns: string[];
  rows: unknown[][];
  nextAfterId: number | null;
}

const HEALTH_TONE: Record<string, string> = { RED: "Off track", AMBER: "At risk", GREEN: "On track" };

export const DashboardsPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const [tab, setTab] = useState("dash");
  const admin = isAdminOf(ctx);
  const catalog = useLoad(() => api<Catalog>("GET", "/api/reports/catalog"), []);
  const cat = catalog.data;
  return (
    <>
      <Head icon={<DataBarVertical20Regular />} title="Dashboards" sub="Charts, saved reports, portfolio health and data exports.">
        <span className={styles.muted}>{admin ? "You can manage everything here" : "Some actions need a workspace admin"}</span>
      </Head>
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          ["dash", "Dashboards"],
          ["reports", "Saved reports"],
          ["exec", "Portfolio health"],
          ["export", "Data exports"],
        ]}
      />
      <Page>
        {catalog.error && <Wait loading={false} error={catalog.error} onRetry={catalog.reload} />}
        {tab === "dash" && <DashboardsTab ctx={ctx} cat={cat} />}
        {tab === "reports" && <SavedReportsTab ctx={ctx} cat={cat} />}
        {tab === "exec" && <PortfolioTab ctx={ctx} />}
        {tab === "export" && <ExportsTab ctx={ctx} cat={cat} admin={admin} />}
      </Page>
    </>
  );
};

const REPORT_HINT = 'Optional settings as JSON, for example {"projectId": 5}';

const DashboardsTab: React.FC<{ ctx: PageCtx; cat: Catalog | null }> = ({ ctx, cat }) => {
  const act = useAct(ctx.notify);
  const [sel, setSel] = useState<number | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [nd, setNd] = useState({ name: "", shared: false, scope: "WORKSPACE", project: "" });
  const [wOpen, setWOpen] = useState(false);
  const [nw, setNw] = useState({ type: "BAR", title: "", report: "TASK_COMPLETION", definition: "", span: "1" });
  const [manage, setManage] = useState(false);
  const dashes = useLoad(async () => (await crud.list<Dash>("reporting/rpt/dashboard", { workspaceId: ctx.workspaceId, sort: "name" })).items, [ctx.workspaceId]);
  const list = dashes.data ?? [];
  const cur = list.find((d) => d.id === sel) ?? list.find((d) => d.isDefault) ?? list[0] ?? null;
  const data = useLoad(
    () => (cur ? api<{ dashboard: { id: number; name: string }; widgets: WidgetData[] }>("GET", `/api/reports/dashboards/${cur.id}/data`) : Promise.resolve(null)),
    [cur?.id],
  );
  const widgets = useLoad(async () => (cur ? (await crud.list<WidgetRow>("reporting/rpt/widget", { dashboardId: cur.id, sort: "sortOrder" })).items : []), [cur?.id]);
  const reload = () => {
    dashes.reload();
    data.reload();
    widgets.reload();
  };
  const types = cat?.widgetTypes?.length ? cat.widgetTypes : ["TABLE", "BAR", "LINE", "PIE", "KPI"];

  const move = async (i: number, dir: -1 | 1) => {
    const rows = widgets.data ?? [];
    const a = rows[i];
    const b = rows[i + dir];
    if (!a || !b) return;
    const ao = a.sortOrder === b.sortOrder ? a.sortOrder + dir : a.sortOrder;
    if (await act(async () => {
      await crud.update("reporting/rpt/widget", a.id, { sortOrder: b.sortOrder });
      await crud.update("reporting/rpt/widget", b.id, { sortOrder: ao });
    })) reload();
  };

  return (
    <>
      <div className={tp.card}>
        <div className={tp.rowBetween}>
          <div className={tp.actions}>
            <Select aria-label="Dashboard" value={cur ? String(cur.id) : ""} onChange={(_, d) => setSel(Number(d.value))}>
              {list.length === 0 && <option value="">No dashboards yet</option>}
              {list.map((d) => (
                <option key={d.id} value={String(d.id)}>
                  {d.name}
                  {d.isDefault ? " (default)" : ""}
                </option>
              ))}
            </Select>
            {cur && <Chip text={cur.isShared ? "Shared" : "Private"} />}
          </div>
          <div className={tp.actions}>
            <Button icon={<ArrowSync20Regular />} onClick={reload} aria-label="Refresh dashboard">
              Refresh
            </Button>
            {cur && (
              <Button onClick={() => setManage(true)}>Widgets</Button>
            )}
            {cur && (
              <Button icon={<Add20Regular />} onClick={() => setWOpen(true)}>
                Add widget
              </Button>
            )}
            {cur && !cur.isDefault && (
              <Button
                onClick={async () => {
                  if (await act(() => crud.update("reporting/rpt/dashboard", cur.id, { isDefault: true }), "Set as default.")) reload();
                }}
              >
                Make default
              </Button>
            )}
            {cur && (
              <Button
                appearance="subtle"
                icon={<Delete20Regular />}
                aria-label="Delete dashboard"
                onClick={async () => {
                  if (await act(() => crud.remove("reporting/rpt/dashboard", cur.id), "Dashboard deleted.")) {
                    setSel(null);
                    reload();
                  }
                }}
              />
            )}
            <Button className={styles.primary} appearance="primary" icon={<Add20Regular />} onClick={() => setNewOpen(true)}>
              New dashboard
            </Button>
          </div>
        </div>
      </div>
      <Wait loading={(dashes.loading && !dashes.data) || (data.loading && !data.data)} error={dashes.error || data.error} empty={!dashes.loading && list.length === 0 && "Create a dashboard, then add widgets that show a report as a chart, number or table."} onRetry={reload} />
      {data.data && data.data.widgets.length === 0 && <div className={styles.emptyRow}>This dashboard has no widgets yet. Use “Add widget”.</div>}
      {data.data && data.data.widgets.length > 0 && (
        <div className={ad.widgetGrid}>
          {data.data.widgets.map((w) => (
            <WidgetView key={w.id} w={w} />
          ))}
        </div>
      )}

      <FormDialog
        open={newOpen}
        title="New dashboard"
        action="Create"
        disabled={!nd.name.trim() || (nd.scope === "PROJECT" && !nd.project)}
        onClose={() => setNewOpen(false)}
        onSubmit={async () => {
          if (
            await act(
              () =>
                crud.create("reporting/rpt/dashboard", {
                  workspaceId: ctx.workspaceId,
                  name: nd.name.trim(),
                  ownerUserId: ctx.me.id,
                  isShared: nd.shared,
                  scopeType: nd.scope,
                  projectId: nd.scope === "PROJECT" ? Number(nd.project) : null,
                }),
              "Dashboard created.",
            )
          ) {
            setNewOpen(false);
            setNd({ name: "", shared: false, scope: "WORKSPACE", project: "" });
            dashes.reload();
          }
        }}
      >
        <Field label="Name" required>
          <Input value={nd.name} onChange={(_, d) => setNd({ ...nd, name: d.value })} placeholder="Delivery overview" />
        </Field>
        <Field label="Covers">
          <Select value={nd.scope} onChange={(_, d) => setNd({ ...nd, scope: d.value })}>
            <option value="WORKSPACE">The whole workspace</option>
            <option value="PROJECT">One project</option>
          </Select>
        </Field>
        {nd.scope === "PROJECT" && (
          <Field label="Project" required>
            <Select value={nd.project} onChange={(_, d) => setNd({ ...nd, project: d.value })}>
              <option value="">Choose a project</option>
              {ctx.projects.map((p) => (
                <option key={p.id} value={String(p.id)}>
                  {p.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Switch checked={nd.shared} label="Share with the workspace" onChange={(_, d) => setNd({ ...nd, shared: d.checked })} />
      </FormDialog>

      <FormDialog
        open={wOpen}
        title="Add widget"
        action="Add"
        disabled={!nw.title.trim()}
        onClose={() => setWOpen(false)}
        onSubmit={async () => {
          let def: unknown = {};
          if (nw.definition.trim()) {
            try {
              def = JSON.parse(nw.definition) as unknown;
            } catch {
              ctx.notify("error", "The report settings must be valid JSON.");
              return;
            }
          }
          if (
            cur &&
            (await act(
              () =>
                crud.create("reporting/rpt/widget", {
                  workspaceId: ctx.workspaceId,
                  dashboardId: cur.id,
                  widgetType: nw.type,
                  title: nw.title.trim(),
                  configJson: JSON.stringify({ report: nw.report, definition: def }),
                  layoutJson: JSON.stringify({ span: Number(nw.span) }),
                  sortOrder: ((widgets.data ?? []).reduce((m, x) => Math.max(m, x.sortOrder), 0) || 0) + 10,
                }),
              "Widget added.",
            ))
          ) {
            setWOpen(false);
            setNw({ ...nw, title: "", definition: "" });
            reload();
          }
        }}
      >
        <Field label="Title" required>
          <Input value={nw.title} onChange={(_, d) => setNw({ ...nw, title: d.value })} placeholder="Overdue work by project" />
        </Field>
        <div className={styles.formRow}>
          <Field label="Show as" className={styles.grow}>
            <Select value={nw.type} onChange={(_, d) => setNw({ ...nw, type: d.value })}>
              {types.map((t) => (
                <option key={t} value={t}>
                  {nice(t)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Width" className={styles.grow}>
            <Select value={nw.span} onChange={(_, d) => setNw({ ...nw, span: d.value })}>
              <option value="1">Half</option>
              <option value="2">Full</option>
            </Select>
          </Field>
        </div>
        <Field label="Report">
          <Select value={nw.report} onChange={(_, d) => setNw({ ...nw, report: d.value })}>
            {(cat?.reportTypes ?? ["TASK_COMPLETION"]).map((t) => (
              <option key={t} value={t}>
                {nice(t)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Report settings" hint={REPORT_HINT}>
          <Textarea className={ad.mono} rows={3} value={nw.definition} onChange={(_, d) => setNw({ ...nw, definition: d.value })} />
        </Field>
      </FormDialog>

      <FormDialog open={manage} title="Widgets on this dashboard" action="Done" onClose={() => setManage(false)} onSubmit={() => setManage(false)}>
        <Wait loading={widgets.loading && !widgets.data} error={widgets.error} empty={!widgets.loading && (widgets.data ?? []).length === 0 && "No widgets"} />
        {(widgets.data ?? []).map((w, i, all) => (
          <div key={w.id} className={tp.item}>
            <div className={tp.itemMain}>
              <span className={tp.itemTitle}>{w.title || nice(w.widgetType)}</span>
              <span className={tp.itemMeta}>{nice(w.widgetType)}</span>
            </div>
            <Button size="small" appearance="subtle" icon={<ArrowUp16Regular />} aria-label="Move up" disabled={i === 0} onClick={() => void move(i, -1)} />
            <Button size="small" appearance="subtle" icon={<ArrowDown16Regular />} aria-label="Move down" disabled={i === all.length - 1} onClick={() => void move(i, 1)} />
            <Button
              size="small"
              appearance="subtle"
              icon={<Delete20Regular />}
              aria-label="Delete widget"
              onClick={async () => {
                if (await act(() => crud.remove("reporting/rpt/widget", w.id), "Widget deleted.")) reload();
              }}
            />
          </div>
        ))}
      </FormDialog>
    </>
  );
};

const SavedReportsTab: React.FC<{ ctx: PageCtx; cat: Catalog | null }> = ({ ctx, cat }) => {
  const act = useAct(ctx.notify);
  const [run, setRun] = useState<{ name: string; out: ReportOut } | null>(null);
  const [running, setRunning] = useState<number | null>(null);
  const reports = useLoad(async () => (await crud.list<SavedReport>("reporting/rpt/report", { workspaceId: ctx.workspaceId, sort: "name" })).items, [ctx.workspaceId]);
  const reportOptions: [string, string][] = (reports.data ?? []).map((r) => [String(r.id), r.name]);
  const types: [string, string][] = (cat?.reportTypes ?? []).map((t) => [t, nice(t)]);
  return (
    <>
      <EntityTable
        ctx={ctx}
        route="reporting/rpt/report"
        title="Saved reports"
        noun="report"
        sort="name"
        query={{ workspaceId: ctx.workspaceId }}
        defaults={{ workspaceId: ctx.workspaceId, ownerUserId: ctx.me.id }}
        search
        fields={[
          { key: "name", label: "Name", required: true },
          { key: "reportType", label: "Report", kind: "select", options: types, required: true },
          { key: "isShared", label: "Shared", kind: "bool" },
          { key: "definitionJson", label: "Settings", kind: "json", hint: REPORT_HINT, list: false },
        ]}
        rowActions={(r) => (
          <Button
            size="small"
            disabled={running === r.id}
            onClick={async () => {
              setRunning(r.id);
              await act(async () => setRun({ name: str(r.name), out: await api<ReportOut>("GET", `/api/reports/${r.id}/run`) }));
              setRunning(null);
            }}
          >
            {running === r.id ? "Running…" : "Run"}
          </Button>
        )}
      />
      {run && (
        <div className={tp.card}>
          <div className={tp.rowBetween}>
            <h3 className={tp.cardTitle}>
              {run.name} · {run.out.count} {run.out.count === 1 ? "row" : "rows"}
            </h3>
            <div className={tp.actions}>
              <Button icon={<ArrowDownload20Regular />} onClick={() => downloadCsv(`${run.name.replace(/\W+/g, "-").toLowerCase()}-${iso(new Date())}.csv`, run.out.columns, run.out.rows)}>
                Download CSV
              </Button>
              <Button appearance="subtle" onClick={() => setRun(null)}>
                Close
              </Button>
            </div>
          </div>
          <DataTable columns={run.out.columns} rows={run.out.rows} />
        </div>
      )}
      <EntityTable
        ctx={ctx}
        route="reporting/rpt/reportschedule"
        title="Report schedules"
        noun="schedule"
        query={{ workspaceId: ctx.workspaceId }}
        defaults={{ workspaceId: ctx.workspaceId }}
        emptyText="No schedules. Add one to e-mail a saved report on a timetable."
        fields={[
          { key: "reportId", label: "Report", kind: "select", options: reportOptions, required: true },
          { key: "cron", label: "When (cron)", required: true, placeholder: "0 8 * * 1", hint: "Minute hour day month weekday, e.g. 0 8 * * 1 = Mondays at 08:00" },
          { key: "format", label: "Format", kind: "select", required: true, options: [["CSV", "CSV"], ["JSON", "JSON"]] },
          { key: "recipientsJson", label: "Send to", kind: "json", required: true, placeholder: '["name@company.com"]', hint: "A JSON list of e-mail addresses" },
          { key: "isActive", label: "On", kind: "bool" },
          { key: "nextRunAt", label: "Next run", kind: "datetime", form: false },
          { key: "lastRunAt", label: "Last run", kind: "datetime", form: false },
        ]}
      />
      <EntityTable
        ctx={ctx}
        route="reporting/rpt/executivepack"
        title="Executive packs"
        noun="pack"
        query={{ workspaceId: ctx.workspaceId }}
        defaults={{ workspaceId: ctx.workspaceId }}
        emptyText="No packs. A pack bundles several saved reports into one mailing."
        fields={[
          { key: "name", label: "Name", required: true },
          { key: "reportIdsJson", label: "Reports", kind: "json", required: true, placeholder: "[1, 2, 3]", hint: `A JSON list of saved report ids${reportOptions.length ? `: ${reportOptions.map(([k, n]) => `${k} = ${n}`).join(", ")}` : ""}` },
          { key: "scheduleCron", label: "When (cron)", placeholder: "0 8 1 * *" },
          { key: "recipientsJson", label: "Send to", kind: "json", placeholder: '["boss@company.com"]' },
        ]}
      />
    </>
  );
};

const PortfolioTab: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const [pf, setPf] = useState<number | null>(null);
  const exec = useLoad(() => api<Exec>("GET", "/api/reports/executive/portfolio"), []);
  const portfolios = useLoad(async () => (await crud.list<{ id: number; name: string }>("workspaces/ws/portfolio", { workspaceId: ctx.workspaceId, sort: "name" })).items, [ctx.workspaceId]);
  const list = portfolios.data ?? [];
  const sel = pf ?? list[0]?.id ?? null;
  const roll = useLoad(() => (sel === null ? Promise.resolve(null) : api<Rollup>("GET", `/api/workspaces/portfolios/${sel}/rollup`)), [sel]);
  const t = exec.data?.totals;
  const tiles = (x: Exec["totals"]) => (
    <div className={tp.tiles}>
      <Tile n={x.projects} label="Projects" />
      <Tile n={`${x.percentComplete}%`} label="Complete" />
      <Tile n={x.overdue} label="Overdue tasks" />
      <Tile n={x.red} label="Off track" />
      <Tile n={x.amber} label="At risk" />
      <Tile n={x.green} label="On track" />
    </div>
  );
  return (
    <>
      <div className={tp.card}>
        <h3 className={tp.cardTitle}>All my workspaces</h3>
        <Wait loading={exec.loading && !exec.data} error={exec.error} onRetry={exec.reload} />
        {t && tiles(t)}
        {exec.data && exec.data.projects.length > 0 && (
          <>
            <DataTable columns={exec.data.columns} rows={exec.data.projects} />
            <div className={tp.actions}>
              <Button icon={<ArrowDownload20Regular />} onClick={() => exec.data && downloadCsv(`portfolio-health-${iso(new Date())}.csv`, exec.data.columns, exec.data.projects)}>
                Download CSV
              </Button>
            </div>
          </>
        )}
      </div>
      <div className={tp.card}>
        <div className={tp.rowBetween}>
          <h3 className={tp.cardTitle}>Portfolio roll-up</h3>
          <Select aria-label="Portfolio" value={sel === null ? "" : String(sel)} onChange={(_, d) => setPf(Number(d.value))}>
            {list.length === 0 && <option value="">No portfolios</option>}
            {list.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.name}
              </option>
            ))}
          </Select>
        </div>
        <Wait loading={roll.loading && !roll.data} error={roll.error} empty={!roll.loading && list.length === 0 && "Create a portfolio under People › Structure to see its roll-up here."} onRetry={roll.reload} />
        {roll.data && (
          <>
            {tiles(roll.data.totals)}
            <div className={tp.scroll}>
              <table className={tp.tbl}>
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Health</th>
                    <th>Progress</th>
                    <th>Overdue</th>
                    <th>Remaining</th>
                    <th>Ends</th>
                  </tr>
                </thead>
                <tbody>
                  {roll.data.projects.map((p) => (
                    <tr key={p.projectId}>
                      <td className={tp.strong}>{p.name}</td>
                      <td>
                        <span className={`${tp.chip} ${p.health === "RED" ? tp.chipRed : p.health === "AMBER" ? tp.chipAmber : tp.chipGreen}`}>{HEALTH_TONE[p.health] ?? p.health}</span>
                      </td>
                      <td>
                        {p.doneTasks}/{p.totalTasks} · {p.percentComplete}%
                      </td>
                      <td className={p.overdueTasks > 0 ? tp.late : undefined}>{p.overdueTasks}</td>
                      <td>{Math.round(p.remainingMinutes / 60)} h</td>
                      <td>{fmtDate(p.endDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
};

const ExportsTab: React.FC<{ ctx: PageCtx; cat: Catalog | null; admin: boolean }> = ({ ctx, cat, admin }) => {
  const act = useAct(ctx.notify);
  const datasets = cat?.datasets ?? [];
  const [ds, setDs] = useState("tasks");
  const [fmt, setFmt] = useState("CSV");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [since, setSince] = useState("");
  const [feed, setFeed] = useState<Feed | null>(null);
  const [feedBusy, setFeedBusy] = useState(false);
  const jobsKey = useState(0);
  const loadFeed = async (after: number) => {
    setFeedBusy(true);
    await act(async () => setFeed(await api<Feed>("GET", `/api/reports/bi/feed/${ds}`, undefined, { workspaceId: ctx.workspaceId, afterId: after, pageSize: 100, ...(since ? { updatedSince: new Date(since).toISOString() } : {}) })));
    setFeedBusy(false);
  };
  return (
    <>
      {!admin && <div className={ad.note}>Exports and the BI feed are for workspace admins.</div>}
      <div className={tp.card}>
        <h3 className={tp.cardTitle}>Export a whole dataset</h3>
        <p className={tp.text}>Builds a file with up to 50,000 rows and keeps it for download below.</p>
        <div className={tp.formLine}>
          <Field label="Dataset">
            <Select value={ds} onChange={(_, d) => (setDs(d.value), setFeed(null))}>
              {(datasets.length ? datasets : ["tasks"]).map((x) => (
                <option key={x} value={x}>
                  {nice(x)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Format">
            <Select value={fmt} onChange={(_, d) => setFmt(d.value)}>
              {(cat?.formats ?? ["CSV", "JSON"]).map((x) => (
                <option key={x}>{x}</option>
              ))}
            </Select>
          </Field>
          <Field label="Name (optional)" className={styles.grow}>
            <Input value={name} onChange={(_, d) => setName(d.value)} />
          </Field>
          <Button
            className={styles.primary}
            appearance="primary"
            icon={<ArrowDownload20Regular />}
            disabled={!admin || busy}
            onClick={async () => {
              setBusy(true);
              const ok = await act(async () => {
                const r = await api<{ jobId: number; rows: number }>("POST", "/api/reports/bi/export", { workspaceId: ctx.workspaceId, dataset: ds, format: fmt, name: name.trim() || null });
                await download(`/api/reports/exports/${r.jobId}/download`, {}, `${ds}.${fmt.toLowerCase()}`);
                ctx.notify("success", `Exported ${r.rows.toLocaleString()} rows.`);
              });
              setBusy(false);
              if (ok) jobsKey[1]((n) => n + 1);
            }}
          >
            {busy ? "Exporting…" : "Export and download"}
          </Button>
        </div>
      </div>
      <EntityTable
        key={jobsKey[0]}
        ctx={ctx}
        route="reporting/rpt/biexportjob"
        title="Earlier exports"
        noun="export"
        sort="-id"
        query={{ workspaceId: ctx.workspaceId }}
        canWrite={false}
        canCreate={false}
        canDelete={false}
        emptyText="No exports yet"
        fields={[
          { key: "name", label: "Name" },
          { key: "dataset", label: "Dataset" },
          { key: "format", label: "Format" },
          { key: "status", label: "Status" },
          { key: "rowCount", label: "Rows" },
          { key: "createdAt", label: "Created", kind: "datetime" },
        ]}
        rowActions={(r) => (
          <Button size="small" icon={<ArrowDownload20Regular />} onClick={() => void act(() => download(`/api/reports/exports/${r.id}/download`, {}, `${str(r.dataset)}.${str(r.format).toLowerCase()}`))}>
            Download
          </Button>
        )}
      />
      <div className={tp.card}>
        <h3 className={tp.cardTitle}>BI feed preview</h3>
        <p className={tp.text}>The same feed Power BI or any other tool reads: <code>/api/reports/bi/feed/{ds}</code>, in pages, newest changes first with “updated since”.</p>
        <div className={tp.formLine}>
          <Field label="Updated since (optional)">
            <Input type="datetime-local" value={since} onChange={(_, d) => setSince(d.value)} />
          </Field>
          <Button disabled={!admin || feedBusy} onClick={() => void loadFeed(0)}>
            {feedBusy ? "Loading…" : "Load first page"}
          </Button>
          {feed?.nextAfterId != null && (
            <Button disabled={feedBusy} onClick={() => void loadFeed(feed.nextAfterId ?? 0)}>
              Next page
            </Button>
          )}
        </div>
        {feed && (
          <>
            <div className={styles.muted}>{feed.rows.length} rows{feed.nextAfterId === null ? " (that is everything)" : ""}</div>
            <DataTable columns={feed.columns} rows={feed.rows} max={100} />
          </>
        )}
      </div>
    </>
  );
};

/* =================================================================== *
 *  People: members, teams, structure, share links
 * =================================================================== */
interface MemberRow {
  id: number;
  userId: number;
  role: string;
  isActive: boolean;
  joinedAt?: string | null;
}
interface UserRow {
  id: number;
  displayName?: string | null;
  email?: string | null;
}

export const PeoplePage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const [tab, setTab] = useState("members");
  const admin = isAdminOf(ctx);
  return (
    <>
      <Head icon={<People20Regular />} title="People" sub="Who is in the workspace, how they are grouped and what you have shared.">
        <span className={styles.muted}>{admin ? "You’re an admin of this workspace" : "Only admins can change these"}</span>
      </Head>
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          ["members", "Members"],
          ["teams", "Teams"],
          ["structure", "Portfolios & folders"],
          ["share", "Share links"],
        ]}
      />
      <Page>
        {tab === "members" && <MembersTab ctx={ctx} admin={admin} />}
        {tab === "teams" && <TeamsTab ctx={ctx} />}
        {tab === "structure" && <StructureTab ctx={ctx} />}
        {tab === "share" && <ShareTab ctx={ctx} admin={admin} />}
      </Page>
    </>
  );
};

const MembersTab: React.FC<{ ctx: PageCtx; admin: boolean }> = ({ ctx, admin }) => {
  const act = useAct(ctx.notify);
  const [inv, setInv] = useState<{ email: string; role: string } | null>(null);
  const [q, setQ] = useState("");
  const l = useLoad(async () => {
    const members = (await crud.list<MemberRow>(R.member, { workspaceId: ctx.workspaceId, pageSize: 500 })).items;
    const users = members.length ? (await crud.list<UserRow>(R.user, { "id.in": members.map((m) => m.userId).join(","), pageSize: 500 })).items : [];
    const by = new Map(users.map((u) => [u.id, u]));
    return members.map((m) => ({ ...m, name: by.get(m.userId)?.displayName || by.get(m.userId)?.email || `User ${m.userId}`, email: by.get(m.userId)?.email ?? "" }));
  }, [ctx.workspaceId]);
  const rows = (l.data ?? []).filter((m) => `${m.name} ${m.email}`.toLowerCase().includes(q.trim().toLowerCase()));
  const set = async (id: number, body: Record<string, unknown>) => {
    if (await act(() => crud.update(R.member, id, body), "Saved.")) {
      l.reload();
      ctx.reloadProjects();
    }
  };
  return (
    <div className={tp.card}>
      <div className={tp.rowBetween}>
        <h3 className={tp.cardTitle}>Workspace members ({(l.data ?? []).length})</h3>
        <div className={tp.actions}>
          <SearchBox className={styles.search} placeholder="Search" value={q} onChange={(_, d) => setQ(d.value)} />
          <Button className={styles.primary} appearance="primary" icon={<Add20Regular />} disabled={!admin} onClick={() => setInv({ email: "", role: "MEMBER" })}>
            Invite
          </Button>
        </div>
      </div>
      <Wait loading={l.loading && !l.data} error={l.error} empty={!l.loading && rows.length === 0 && "No members found"} onRetry={l.reload} />
      {rows.length > 0 && (
        <div className={tp.scroll}>
          <table className={tp.tbl}>
            <thead>
              <tr>
                <th>Name</th>
                <th>E-mail</th>
                <th>Role</th>
                <th>Joined</th>
                <th>Active</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m.id} className={m.isActive ? undefined : ad.dim}>
                  <td className={tp.strong}>{m.name}</td>
                  <td>{m.email || "—"}</td>
                  <td>
                    <Select aria-label={`Role of ${m.name}`} value={m.role} disabled={!admin || m.userId === ctx.me.id} onChange={(_, d) => void set(m.id, { role: d.value })}>
                      {ROLES.map(([k, label]) => (
                        <option key={k} value={k}>
                          {label}
                        </option>
                      ))}
                    </Select>
                  </td>
                  <td>{fmtDate(m.joinedAt)}</td>
                  <td>
                    <Switch checked={m.isActive} disabled={!admin || m.userId === ctx.me.id} aria-label={`${m.name} is ${m.isActive ? "active" : "removed"}`} onChange={(_, d) => void set(m.id, { isActive: d.checked })} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <FormDialog
        open={inv !== null}
        title="Invite to workspace"
        action="Invite"
        disabled={!inv?.email.trim()}
        onClose={() => setInv(null)}
        onSubmit={async () => {
          if (!inv) return;
          try {
            await api("POST", `/api/workspaces/${ctx.workspaceId}/members/invite`, { email: inv.email.trim(), role: inv.role });
            ctx.notify("success", `${inv.email.trim()} was added.`);
            setInv(null);
            l.reload();
          } catch (e) {
            ctx.notify("error", e instanceof ApiError && e.status === 404 ? "That person has not signed in to the Task Manager yet." : msgOf(e));
          }
        }}
      >
        <Field label="Email" required hint="They need to have signed in once.">
          <Input type="email" value={inv?.email ?? ""} onChange={(_, d) => setInv((s) => (s ? { ...s, email: d.value } : s))} />
        </Field>
        <Field label="Role">
          <Select value={inv?.role ?? "MEMBER"} onChange={(_, d) => setInv((s) => (s ? { ...s, role: d.value } : s))}>
            {ROLES.map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </Select>
        </Field>
      </FormDialog>
    </div>
  );
};

const TeamsTab: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const [team, setTeam] = useState<Row | null>(null);
  const [add, setAdd] = useState("");
  const members = useLoad(async () => (team ? (await crud.list<{ id: number; userId: number }>("workspaces/ws/teammember", { teamId: team.id, pageSize: 500 })).items : []), [team?.id]);
  const nameOf = (id: number) => ctx.people.find((p) => p.id === id)?.name ?? `User ${id}`;
  const have = new Set((members.data ?? []).map((m) => m.userId));
  return (
    <>
      <EntityTable
        ctx={ctx}
        route="workspaces/ws/team"
        title="Teams"
        noun="team"
        sort="name"
        search
        query={{ workspaceId: ctx.workspaceId }}
        defaults={{ workspaceId: ctx.workspaceId }}
        fields={[
          { key: "name", label: "Name", required: true },
          { key: "description", label: "Description", kind: "textarea" },
          { key: "leadUserId", label: "Lead", kind: "user" },
        ]}
        rowActions={(r) => (
          <Button size="small" onClick={() => setTeam(r)}>
            Members
          </Button>
        )}
      />
      <FormDialog open={team !== null} title={team ? `${str(team.name)} · members` : "Members"} action="Done" onClose={() => setTeam(null)} onSubmit={() => setTeam(null)}>
        <div className={tp.formLine}>
          <Select aria-label="Person to add" value={add} onChange={(_, d) => setAdd(d.value)}>
            <option value="">Add a person…</option>
            {ctx.people.filter((p) => !have.has(p.id)).map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.name}
              </option>
            ))}
          </Select>
          <Button
            disabled={!add || !team}
            onClick={async () => {
              if (team && (await act(() => crud.create("workspaces/ws/teammember", { workspaceId: ctx.workspaceId, teamId: team.id, userId: Number(add) }), "Added."))) {
                setAdd("");
                members.reload();
              }
            }}
          >
            Add
          </Button>
        </div>
        <Wait loading={members.loading && !members.data} error={members.error} empty={!members.loading && (members.data ?? []).length === 0 && "No one in this team yet"} />
        {(members.data ?? []).map((m) => (
          <div key={m.id} className={tp.item}>
            <span className={tp.itemMain}>{nameOf(m.userId)}</span>
            <Button
              size="small"
              appearance="subtle"
              icon={<Delete20Regular />}
              aria-label={`Remove ${nameOf(m.userId)}`}
              onClick={async () => {
                if (await act(() => crud.remove("workspaces/ws/teammember", m.id), "Removed.")) members.reload();
              }}
            />
          </div>
        ))}
      </FormDialog>
    </>
  );
};

const StructureTab: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const [pf, setPf] = useState<Row | null>(null);
  const [add, setAdd] = useState("");
  const folders = useLoad(async () => (await crud.list<{ id: number; name: string }>("workspaces/prj/folder", { workspaceId: ctx.workspaceId, sort: "name" })).items, [ctx.workspaceId]);
  const links = useLoad(async () => (pf ? (await crud.list<{ id: number; projectId: number }>("workspaces/prj/portfolioproject", { portfolioId: pf.id, pageSize: 500 })).items : []), [pf?.id]);
  const have = new Set((links.data ?? []).map((x) => x.projectId));
  const folderOptions: [string, string][] = (folders.data ?? []).map((f) => [String(f.id), f.name]);
  const STATUS: [string, string][] = [
    ["ACTIVE", "Active"],
    ["ON_HOLD", "On hold"],
    ["COMPLETED", "Completed"],
    ["ARCHIVED", "Archived"],
  ];
  return (
    <>
      <EntityTable
        ctx={ctx}
        route="workspaces/ws/portfolio"
        title="Portfolios"
        noun="portfolio"
        sort="name"
        search
        query={{ workspaceId: ctx.workspaceId }}
        defaults={{ workspaceId: ctx.workspaceId, ownerUserId: ctx.me.id }}
        fields={[
          { key: "name", label: "Name", required: true },
          { key: "description", label: "Description", kind: "textarea", list: false },
          { key: "status", label: "Status", kind: "select", options: STATUS, required: true },
          { key: "health", label: "Health", kind: "select", options: [["GREEN", "On track"], ["AMBER", "At risk"], ["RED", "Off track"]] },
          { key: "startDate", label: "Starts", kind: "date" },
          { key: "endDate", label: "Ends", kind: "date" },
        ]}
        rowActions={(r) => (
          <Button size="small" onClick={() => setPf(r)}>
            Projects
          </Button>
        )}
      />
      <EntityTable
        ctx={ctx}
        route="workspaces/prj/folder"
        title="Folders"
        noun="folder"
        sort="name"
        search
        query={{ workspaceId: ctx.workspaceId }}
        defaults={{ workspaceId: ctx.workspaceId }}
        fields={[
          { key: "name", label: "Name", required: true },
          { key: "parentFolderId", label: "Inside folder", kind: "select", options: folderOptions },
          { key: "sortOrder", label: "Order", kind: "number" },
          { key: "isArchived", label: "Archived", kind: "bool" },
        ]}
      />
      <FormDialog open={pf !== null} title={pf ? `${str(pf.name)} · projects` : "Projects"} action="Done" onClose={() => setPf(null)} onSubmit={() => setPf(null)}>
        <div className={tp.formLine}>
          <Select aria-label="Project to add" value={add} onChange={(_, d) => setAdd(d.value)}>
            <option value="">Add a project…</option>
            {ctx.projects.filter((p) => !have.has(p.id)).map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.name}
              </option>
            ))}
          </Select>
          <Button
            disabled={!add || !pf}
            onClick={async () => {
              if (pf && (await act(() => crud.create("workspaces/prj/portfolioproject", { workspaceId: ctx.workspaceId, portfolioId: pf.id, projectId: Number(add) }), "Added."))) {
                setAdd("");
                links.reload();
              }
            }}
          >
            Add
          </Button>
        </div>
        <Wait loading={links.loading && !links.data} error={links.error} empty={!links.loading && (links.data ?? []).length === 0 && "No projects in this portfolio yet"} />
        {(links.data ?? []).map((x) => {
          const name = ctx.projects.find((p) => p.id === x.projectId)?.name ?? `Project ${x.projectId}`;
          return (
            <div key={x.id} className={tp.item}>
              <span className={tp.itemMain}>{name}</span>
              <Button
                size="small"
                appearance="subtle"
                icon={<Delete20Regular />}
                aria-label={`Remove ${name}`}
                onClick={async () => {
                  if (await act(() => crud.remove("workspaces/prj/portfolioproject", x.id), "Removed.")) links.reload();
                }}
              />
            </div>
          );
        })}
      </FormDialog>
    </>
  );
};

interface LinkRow {
  id: number;
  resourceType: string;
  resourceId: number;
  token: string;
  permission: string;
  expiresAt?: string | null;
  isActive: boolean;
}
interface SharedView {
  name?: string;
  title?: string;
  description?: string | null;
  status?: string;
  startDate?: string | null;
  endDate?: string | null;
  dueDate?: string | null;
  priority?: string;
  isCompleted?: boolean;
  tasks?: { id: number; title: string; priority: string; dueDate?: string | null; isCompleted: boolean; statusName?: string | null }[] | null;
  checklists?: { title: string; items: { text: string; isDone: boolean }[] | null }[] | null;
  subtasks?: { id: number; title: string; isCompleted: boolean }[] | null;
}

const ShareTab: React.FC<{ ctx: PageCtx; admin: boolean }> = ({ ctx, admin }) => {
  const act = useAct(ctx.notify);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ type: "PROJECT", project: "", task: "", days: "30" });
  const [made, setMade] = useState<string | null>(null);
  const [view, setView] = useState<{ token: string; data: SharedView | null; error: string } | null>(null);
  const l = useLoad(async () => (await crud.list<LinkRow>("workspaces/ws/sharelink", { workspaceId: ctx.workspaceId, sort: "-id" })).items, [ctx.workspaceId]);
  const urlOf = (t: string) => `${TASKS_API}/api/public/share/${t}`;
  const rid = f.type === "PROJECT" ? Number(f.project) : Number(f.task);
  const nameOf = (r: LinkRow) => (r.resourceType === "PROJECT" ? (ctx.projects.find((p) => p.id === r.resourceId)?.name ?? `Project ${r.resourceId}`) : `Task ${r.resourceId}`);

  const preview = async (token: string) => {
    setView({ token, data: null, error: "" });
    try {
      const res = await fetch(urlOf(token));
      if (!res.ok) throw new Error("gone");
      setView({ token, data: camelize(await res.json()) as SharedView, error: "" });
    } catch {
      setView({ token, data: null, error: "This link doesn’t work any more. It may have expired or been switched off." });
    }
  };

  return (
    <div className={tp.card}>
      <div className={tp.rowBetween}>
        <h3 className={tp.cardTitle}>Guest links</h3>
        <Button className={styles.primary} appearance="primary" icon={<Add20Regular />} disabled={!admin} onClick={() => setOpen(true)}>
          New link
        </Button>
      </div>
      <p className={tp.text}>Anyone with a link can read the shared project or task, without signing in. Private tasks, comments and e-mail addresses are never shown.</p>
      <Wait loading={l.loading && !l.data} error={l.error} empty={!l.loading && (l.data ?? []).length === 0 && "No links yet"} onRetry={l.reload} />
      {(l.data ?? []).map((r) => {
        const expired = !!r.expiresAt && utc(r.expiresAt).getTime() < Date.now();
        return (
          <div key={r.id} className={`${tp.item} ${r.isActive && !expired ? "" : ad.dim}`}>
            <div className={tp.itemMain}>
              <span className={tp.itemTitle}>
                {nameOf(r)} <Chip text={r.resourceType} /> {!r.isActive ? <Chip text="Off" /> : expired ? <Chip text="Expired" /> : null}
              </span>
              <span className={tp.itemMeta}>{r.expiresAt ? `Until ${fmtStamp(r.expiresAt)}` : "Never expires"}</span>
            </div>
            <Button size="small" icon={<Copy20Regular />} onClick={() => copyText(urlOf(r.token), ctx.notify)}>
              Copy link
            </Button>
            <Button size="small" icon={<Open20Regular />} onClick={() => void preview(r.token)}>
              Preview
            </Button>
            <Switch
              checked={r.isActive}
              disabled={!admin}
              aria-label={`Link to ${nameOf(r)} is ${r.isActive ? "on" : "off"}`}
              onChange={async (_, d) => {
                if (await act(() => crud.update("workspaces/ws/sharelink", r.id, { isActive: d.checked }))) l.reload();
              }}
            />
            <Button
              size="small"
              appearance="subtle"
              icon={<Delete20Regular />}
              aria-label="Delete link"
              disabled={!admin}
              onClick={async () => {
                if (await act(() => crud.remove("workspaces/ws/sharelink", r.id), "Link deleted.")) l.reload();
              }}
            />
          </div>
        );
      })}

      <FormDialog
        open={open}
        title="New guest link"
        action="Create link"
        disabled={!(rid > 0)}
        onClose={() => setOpen(false)}
        onSubmit={async () => {
          try {
            const r = await api<{ token: string }>("POST", `/api/workspaces/${ctx.workspaceId}/share-links`, { resourceType: f.type, resourceId: rid, expiresInDays: f.days ? Number(f.days) : null });
            setOpen(false);
            setMade(urlOf(r.token));
            l.reload();
          } catch (e) {
            ctx.notify("error", msgOf(e));
          }
        }}
      >
        <Field label="Share">
          <Select value={f.type} onChange={(_, d) => setF({ ...f, type: d.value })}>
            <option value="PROJECT">A project</option>
            <option value="TASK">A task</option>
          </Select>
        </Field>
        {f.type === "PROJECT" ? (
          <Field label="Project" required>
            <Select value={f.project} onChange={(_, d) => setF({ ...f, project: d.value })}>
              <option value="">Choose a project</option>
              {ctx.projects.map((p) => (
                <option key={p.id} value={String(p.id)}>
                  {p.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : (
          <Field label="Task number (id)" required hint="Open the task and use the id from its address, or copy it from a list.">
            <Input type="number" value={f.task} onChange={(_, d) => setF({ ...f, task: d.value })} />
          </Field>
        )}
        <Field label="Expires after (days)" hint="Leave empty for a link that never expires.">
          <Input type="number" value={f.days} onChange={(_, d) => setF({ ...f, days: d.value })} />
        </Field>
      </FormDialog>

      <FormDialog open={made !== null} title="Link created" action="Copy link" onClose={() => setMade(null)} onSubmit={() => (made && copyText(made, ctx.notify), setMade(null))}>
        <p className={tp.text}>Send this link to your guest. You can switch it off any time.</p>
        <Input readOnly value={made ?? ""} aria-label="Guest link" onFocus={(e) => e.currentTarget.select()} />
      </FormDialog>

      <FormDialog open={view !== null} title="What a guest sees" action="Close" onClose={() => setView(null)} onSubmit={() => setView(null)}>
        {view && !view.data && !view.error && <Spinner size="small" label="Loading…" />}
        {view?.error && <div className={ad.note}>{view.error}</div>}
        {view?.data && (
          <>
            <h3 className={tp.cardTitle}>{view.data.name ?? view.data.title}</h3>
            {view.data.description && <p className={tp.text}>{view.data.description}</p>}
            <div className={styles.muted}>
              {view.data.status ? `${nice(view.data.status)} · ` : ""}
              {view.data.startDate ? `${fmtDate(view.data.startDate)} – ` : ""}
              {fmtDate(view.data.endDate ?? view.data.dueDate)}
            </div>
            {(view.data.tasks ?? []).length > 0 && (
              <table className={tp.tbl}>
                <tbody>
                  {(view.data.tasks ?? []).map((t) => (
                    <tr key={t.id}>
                      <td>
                        <Checkbox checked={t.isCompleted} disabled label={t.title} />
                      </td>
                      <td>{t.statusName ?? ""}</td>
                      <td>{fmtDate(t.dueDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {(view.data.checklists ?? []).map((c, i) => (
              <div key={i} className={tp.block}>
                <div className={tp.blockTitle}>{c.title}</div>
                {(c.items ?? []).map((it, j) => (
                  <Checkbox key={j} checked={it.isDone} disabled label={it.text} />
                ))}
              </div>
            ))}
            {(view.data.subtasks ?? []).length > 0 && (
              <div className={tp.block}>
                <div className={tp.blockTitle}>Subtasks</div>
                {(view.data.subtasks ?? []).map((s) => (
                  <Checkbox key={s.id} checked={s.isCompleted} disabled label={s.title} />
                ))}
              </div>
            )}
          </>
        )}
      </FormDialog>
      {made && <span className={ad.srOnly} aria-live="polite">Link created</span>}
    </div>
  );
};

/* =================================================================== *
 *  Access: permissions, audit log, retention, users, my data
 * =================================================================== */
interface AuditRow {
  id: number;
  userId?: number | null;
  serviceName?: string | null;
  schemaName: string;
  tableName: string;
  operation: string;
  entityKey?: string | null;
  changesJson?: string | null;
  ip?: string | null;
  correlationId?: string | null;
  at: string;
}

export const AccessPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const [tab, setTab] = useState("perms");
  const admin = isAdminOf(ctx);
  return (
    <>
      <Head icon={<ShieldKeyhole20Regular />} title="Access & audit" sub="What you can do, who changed what, and your own data.">
        <span className={styles.muted}>{ctx.me.permissions.length} permissions held</span>
      </Head>
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          ["perms", "Permissions"],
          ["audit", "Audit log"],
          ["users", "Users"],
          ["retention", "Data retention"],
          ["mine", "My data"],
        ]}
      />
      <Page>
        {tab === "perms" && <PermissionsTab ctx={ctx} />}
        {tab === "audit" && <AuditTab ctx={ctx} admin={admin} />}
        {tab === "users" && (
          <EntityTable
            ctx={ctx}
            route={R.user}
            title="Users"
            noun="user"
            sort="displayName"
            search
            canCreate={false}
            canDelete={false}
            canWrite={admin}
            emptyText="No users"
            fields={[
              { key: "displayName", label: "Name", required: true },
              { key: "email", label: "E-mail" },
              { key: "locale", label: "Language" },
              { key: "timeZone", label: "Time zone" },
              { key: "currencyCode", label: "Currency" },
              { key: "isActive", label: "Active", kind: "bool" },
              { key: "lastSeenAt", label: "Last seen", kind: "datetime", form: false },
            ]}
          />
        )}
        {tab === "retention" && (
          <EntityTable
            ctx={ctx}
            route="identity/sec/retentionpolicy"
            title="Data retention"
            noun="policy"
            canWrite={admin}
            emptyText="No retention rules. Data is kept until someone deletes it."
            fields={[
              { key: "entityName", label: "Kind of data", required: true, placeholder: "Notification", createOnly: true },
              { key: "retainDays", label: "Keep for (days)", kind: "number", required: true },
              { key: "action", label: "Then", kind: "select", required: true, options: [["DELETE", "Delete"], ["ARCHIVE", "Archive"]] },
              { key: "isActive", label: "On", kind: "bool" },
            ]}
          />
        )}
        {tab === "mine" && <MyDataTab ctx={ctx} />}
      </Page>
    </>
  );
};

const PermissionsTab: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const mine = useLoad(() => api<{ userId: number; permissions: string[] }>("GET", "/api/identity/permissions/mine"), []);
  const cat = useLoad(() => api<{ prefix: string; workspaceRoles: string[]; permissions: { code: string; description?: string | null }[] }>("GET", "/api/identity/permissions"), []);
  const held = new Set(mine.data?.permissions ?? []);
  const prefix = cat.data?.prefix ?? "";
  const groups = useMemo(() => {
    const g = new Map<string, { code: string; description?: string | null }[]>();
    for (const p of cat.data?.permissions ?? []) {
      const bare = prefix && p.code.startsWith(`${prefix}.`) ? p.code.slice(prefix.length + 1) : p.code;
      const key = bare.split(".")[0] ?? bare;
      g.set(key, [...(g.get(key) ?? []), { ...p, code: bare }]);
    }
    return [...g.entries()];
  }, [cat.data, prefix]);
  return (
    <>
      <div className={tp.card}>
        <h3 className={tp.cardTitle}>Your workspaces</h3>
        <div className={tp.tiles}>
          {ctx.me.workspaces.map((w:any) => (
            <Tile key={w.id} n={nice(w.role)} label={w.name} />
          ))}
        </div>
        <Wait loading={mine.loading && !mine.data} error={mine.error} onRetry={mine.reload} />
        {mine.data && (
          <div className={ad.tags}>
            {mine.data.permissions.map((p) => (
              <span key={p} className={ad.tag}>
                {p}
              </span>
            ))}
            {mine.data.permissions.length === 0 && <span className={styles.muted}>No permissions granted</span>}
          </div>
        )}
      </div>
      <div className={tp.card}>
        <h3 className={tp.cardTitle}>Permission catalogue</h3>
        <p className={tp.text}>Every permission this system checks. Roles are built from these in central auth{cat.data ? `, prefixed with “${cat.data.prefix}.”` : ""}. Ticks show what you hold.</p>
        <Wait loading={cat.loading && !cat.data} error={cat.error && /permission/i.test(cat.error) ? "Only administrators can see the full catalogue." : cat.error} onRetry={cat.reload} />
        {groups.map(([g, list]) => (
          <div key={g} className={tp.block}>
            <div className={tp.blockTitle}>{g}</div>
            {list.map((p) => (
              <div key={p.code} className={ad.permRow}>
                <Checkbox checked={held.has(p.code)} disabled aria-label={`${p.code} ${held.has(p.code) ? "held" : "not held"}`} />
                <code>{p.code}</code>
                <span className={styles.muted}>{p.description ?? ""}</span>
              </div>
            ))}
          </div>
        ))}
        {cat.data && (
          <div className={styles.muted}>Workspace roles: {cat.data.workspaceRoles.map(nice).join(", ")}</div>
        )}
      </div>
    </>
  );
};

const OPS: [string, string][] = [
  ["", "Any action"],
  ["create", "Created"],
  ["update", "Changed"],
  ["delete", "Deleted"],
];

const AuditTab: React.FC<{ ctx: PageCtx; admin: boolean }> = ({ ctx, admin }) => {
  const act = useAct(ctx.notify);
  const [table, setTable] = useState("");
  const [op, setOp] = useState("");
  const [who, setWho] = useState("");
  const [from, setFrom] = useState(() => iso(new Date(Date.now() - 7 * 86400000)));
  const [to, setTo] = useState(() => iso(new Date()));
  const [page, setPage] = useState(1);
  const [one, setOne] = useState<AuditRow | null>(null);
  const query: Query = {
    workspaceId: ctx.workspaceId,
    ...(table.trim() ? { "tableName.contains": table.trim() } : {}),
    ...(op ? { operation: op } : {}),
    ...(who ? { userId: who } : {}),
    ...(from ? { "at.gte": new Date(`${from}T00:00:00`).toISOString() } : {}),
    ...(to ? { "at.lt": new Date(new Date(`${to}T00:00:00`).getTime() + 86400000).toISOString() } : {}),
  };
  const l = useLoad(() => crud.list<AuditRow>("identity/aud/auditlog", { ...query, sort: "-at", page, pageSize: PAGE }), [JSON.stringify(query), page]);
  const items = l.data?.items ?? [];
  const nameOf = (id?: number | null) => (id ? (ctx.people.find((p) => p.id === id)?.name ?? `User ${id}`) : "System");
  return (
    <div className={tp.card}>
      <div className={tp.rowBetween}>
        <h3 className={tp.cardTitle}>Audit log</h3>
        <Button
          icon={<ArrowDownload20Regular />}
          disabled={!admin}
          onClick={() => void act(() => download("/api/identity/audit/export", { workspaceId: ctx.workspaceId, from: new Date(`${from}T00:00:00`).toISOString(), to: new Date(new Date(`${to}T00:00:00`).getTime() + 86400000).toISOString() }, "audit.csv"))}
        >
          Export CSV
        </Button>
      </div>
      <div className={tp.formLine}>
        <Field label="From">
          <Input type="date" value={from} onChange={(_, d) => (setFrom(d.value), setPage(1))} />
        </Field>
        <Field label="To">
          <Input type="date" value={to} onChange={(_, d) => (setTo(d.value), setPage(1))} />
        </Field>
        <Field label="Kind of data">
          <Input value={table} placeholder="Task, Project…" onChange={(_, d) => (setTable(d.value), setPage(1))} />
        </Field>
        <Field label="Action">
          <Select value={op} onChange={(_, d) => (setOp(d.value), setPage(1))}>
            {OPS.map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Person">
          <Select value={who} onChange={(_, d) => (setWho(d.value), setPage(1))}>
            <option value="">Anyone</option>
            {ctx.people.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <Wait loading={l.loading && !l.data} error={l.error} empty={!l.loading && items.length === 0 && "Nothing recorded for these filters"} onRetry={l.reload} />
      {items.length > 0 && (
        <div className={tp.scroll}>
          <table className={tp.tbl}>
            <thead>
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>What</th>
                <th>Item</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className={tp.clickRow} onClick={() => setOne(r)}>
                  <td>{fmtStamp(r.at)}</td>
                  <td>{nameOf(r.userId)}</td>
                  <td>
                    <Chip text={r.operation} /> {r.tableName}
                  </td>
                  <td>{r.entityKey ?? "—"}</td>
                  <td>
                    <Button size="small" appearance="subtle" onClick={() => setOne(r)}>
                      Details
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(page > 1 || items.length === PAGE) && (
        <div className={ad.pager}>
          <Button size="small" icon={<ChevronLeft20Regular />} disabled={page <= 1} onClick={() => setPage((p) => p - 1)} aria-label="Previous page" />
          <span className={styles.muted}>Page {page}</span>
          <Button size="small" icon={<ChevronRight20Regular />} disabled={items.length < PAGE} onClick={() => setPage((p) => p + 1)} aria-label="Next page" />
        </div>
      )}
      <FormDialog open={one !== null} title="Change details" action="Close" onClose={() => setOne(null)} onSubmit={() => setOne(null)}>
        {one && (
          <>
            <div className={styles.muted}>
              {nameOf(one.userId)} · {fmtStamp(one.at)} · {nice(one.operation)} {one.schemaName}.{one.tableName} {one.entityKey ?? ""}
            </div>
            <Pretty text={one.changesJson ?? null} />
            <div className={styles.muted}>
              From {one.ip ?? "unknown address"} · request {one.correlationId ?? "—"} {one.serviceName ? `· ${one.serviceName}` : ""}
            </div>
          </>
        )}
      </FormDialog>
    </div>
  );
};

const MyDataTab: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const [busy, setBusy] = useState(false);
  return (
    <div className={tp.card}>
      <h3 className={tp.cardTitle}>Download my data</h3>
      <p className={tp.text}>A JSON file with everything the Task Manager holds about you: profile, memberships, tasks you created or are assigned, comments, time entries, personal lists, habits, focus sessions and notifications.</p>
      <div className={tp.actions}>
        <Button
          className={styles.primary}
          appearance="primary"
          icon={<ArrowDownload20Regular />}
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            await act(() => download("/api/identity/me/export", {}, "my-task-manager-data.json"), "Your data was downloaded.");
            setBusy(false);
          }}
        >
          {busy ? "Preparing…" : "Download my data"}
        </Button>
      </div>
    </div>
  );
};

/* =================================================================== *
 *  Languages, currencies, translations
 * =================================================================== */
interface LocaleInfo {
  localeCode: string;
  languageCode: string;
  name: string;
  nativeName?: string | null;
  fallbackLocaleCode?: string | null;
  isRtl: boolean;
  dateFormat?: string | null;
  timeFormat?: string | null;
  firstDayOfWeek: number;
  decimalSeparator?: string | null;
  groupSeparator?: string | null;
  numeralSystem?: string | null;
}
interface CurrencyInfo {
  currencyCode: string;
  name: string;
  symbol?: string | null;
  decimalDigits: number;
}

export const LocalizationPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const [tab, setTab] = useState("lang");
  return (
    <>
      <Head icon={<LocalLanguage20Regular />} title="Languages & currencies" sub="Languages, screen text, currencies, exchange rates and translated content.">
        <span className={styles.muted}>You use {ctx.me.locale} · {ctx.me.timeZone}</span>
      </Head>
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          ["lang", "Languages"],
          ["strings", "Screen text"],
          ["money", "Currencies & rates"],
          ["trans", "Translations"],
        ]}
      />
      <Page>
        {tab === "lang" && <LanguagesTab ctx={ctx} />}
        {tab === "strings" && <StringsTab ctx={ctx} />}
        {tab === "money" && <MoneyTab ctx={ctx} />}
        {tab === "trans" && <TranslationsTab ctx={ctx} />}
      </Page>
    </>
  );
};

const LanguagesTab: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const live = useLoad(() => api<LocaleInfo[]>("GET", "/api/localization/locales"), []);
  return (
    <>
      <div className={tp.card}>
        <h3 className={tp.cardTitle}>Languages people can pick</h3>
        <Wait loading={live.loading && !live.data} error={live.error} onRetry={live.reload} />
        {live.data && (
          <div className={tp.scroll}>
            <table className={tp.tbl}>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Language</th>
                  <th>Falls back to</th>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Numbers</th>
                  <th>Direction</th>
                </tr>
              </thead>
              <tbody>
                {live.data.map((x) => (
                  <tr key={x.localeCode}>
                    <td className={tp.strong}>{x.localeCode}</td>
                    <td>
                      {x.name}
                      {x.nativeName ? ` · ${x.nativeName}` : ""}
                    </td>
                    <td>{x.fallbackLocaleCode ?? "—"}</td>
                    <td>{x.dateFormat ?? "—"}</td>
                    <td>{x.timeFormat ?? "—"}</td>
                    <td>
                      1{x.groupSeparator ?? ","}000{x.decimalSeparator ?? "."}5 · {x.numeralSystem ?? "latn"}
                    </td>
                    <td>{x.isRtl ? "Right to left" : "Left to right"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <EntityTable
        ctx={ctx}
        route="localization/loc/locale"
        title="Manage languages"
        noun="language"
        sort="localeCode"
        search
        canWrite={isAdminOf(ctx)}
        fields={[
          { key: "localeCode", label: "Code", required: true, placeholder: "en-NG", createOnly: true },
          { key: "languageCode", label: "Language code", required: true, placeholder: "en" },
          { key: "name", label: "Name", required: true },
          { key: "nativeName", label: "Name in that language" },
          { key: "fallbackLocaleCode", label: "Falls back to", placeholder: "en", list: false },
          { key: "isRtl", label: "Right to left", kind: "bool", list: false },
          { key: "dateFormat", label: "Date format", placeholder: "dd/MM/yyyy", list: false },
          { key: "timeFormat", label: "Time format", placeholder: "HH:mm", list: false },
          { key: "firstDayOfWeek", label: "Week starts (0 = Sunday)", kind: "number", list: false },
          { key: "decimalSeparator", label: "Decimal mark", list: false },
          { key: "groupSeparator", label: "Thousands mark", list: false },
          { key: "numeralSystem", label: "Digits", list: false, placeholder: "latn" },
          { key: "isActive", label: "Available", kind: "bool" },
        ]}
      />
      <EntityTable
        ctx={ctx}
        route="localization/loc/timezone"
        title="Time zones"
        noun="time zone"
        sort="ianaName"
        search
        canWrite={isAdminOf(ctx)}
        fields={[
          { key: "ianaName", label: "Name", required: true, placeholder: "Africa/Lagos" },
          { key: "windowsName", label: "Windows name" },
          { key: "utcOffsetMinutes", label: "UTC offset (minutes)", kind: "number", required: true },
        ]}
      />
    </>
  );
};

const StringsTab: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const locales = useLoad(() => api<LocaleInfo[]>("GET", "/api/localization/locales"), []);
  const [loc, setLoc] = useState("");
  const [ns, setNs] = useState("");
  const [q, setQ] = useState("");
  const [key, setKey] = useState("");
  const [msg, setMsg] = useState<{ key: string; text: string; locale: string } | null>(null);
  const act = useAct(ctx.notify);
  const code = loc || ctx.me.locale || "en";
  const strings = useLoad(() => api<{ locale: string; chain: string[]; strings: Record<string, string> }>("GET", `/api/localization/strings/${encodeURIComponent(code)}`, undefined, ns.trim() ? { namespace: ns.trim() } : undefined), [code, ns]);
  const entries = Object.entries(strings.data?.strings ?? {}).filter(([k, v]) => `${k} ${v}`.toLowerCase().includes(q.trim().toLowerCase()));
  const localeOptions: [string, string][] = (locales.data ?? []).map((x) => [x.localeCode, `${x.name} (${x.localeCode})`]);
  return (
    <>
      <div className={tp.card}>
        <h3 className={tp.cardTitle}>Screen text as a language sees it</h3>
        <div className={tp.formLine}>
          <Field label="Language">
            <Select value={code} onChange={(_, d) => setLoc(d.value)}>
              {localeOptions.length === 0 && <option value={code}>{code}</option>}
              {localeOptions.map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Area (optional)">
            <Input value={ns} placeholder="common, tasks…" onChange={(_, d) => setNs(d.value)} />
          </Field>
          <Field label="Search" className={styles.grow}>
            <Input value={q} onChange={(_, d) => setQ(d.value)} />
          </Field>
        </div>
        <Wait loading={strings.loading && !strings.data} error={strings.error} empty={!strings.loading && entries.length === 0 && "No text found"} onRetry={strings.reload} />
        {strings.data && <div className={styles.muted}>Looks in: {strings.data.chain.join(" → ")} · {entries.length} texts</div>}
        {entries.length > 0 && (
          <div className={tp.scroll}>
            <table className={tp.tbl}>
              <tbody>
                {entries.slice(0, 200).map(([k, v]) => (
                  <tr key={k}>
                    <td className={tp.strong}>{k}</td>
                    <td>{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className={tp.card}>
        <h3 className={tp.cardTitle}>Look up an error message</h3>
        <div className={tp.formLine}>
          <Field label="Message key" className={styles.grow}>
            <Input value={key} placeholder="error.notfound" onChange={(_, d) => setKey(d.value)} />
          </Field>
          <Button
            disabled={!key.trim()}
            onClick={() => void act(async () => setMsg(await api("GET", `/api/localization/messages/${encodeURIComponent(key.trim())}`)))}
          >
            Look up
          </Button>
        </div>
        {msg && (
          <p className={tp.text}>
            <strong>{msg.key}</strong> in {msg.locale}: {msg.text}
          </p>
        )}
      </div>
      <EntityTable
        ctx={ctx}
        route="localization/loc/uistring"
        title="Edit screen text"
        noun="text"
        sort="namespace"
        search
        query={{ ...(code ? { localeCode: code } : {}) }}
        canWrite={isAdminOf(ctx) || ctx.me.permissions.includes("loc.write")}
        defaults={{ localeCode: code }}
        fields={[
          { key: "namespace", label: "Area", required: true, createOnly: true },
          { key: "stringKey", label: "Key", required: true, createOnly: true },
          { key: "localeCode", label: "Language", form: false },
          { key: "textValue", label: "Text", kind: "textarea", required: true },
        ]}
      />
      <EntityTable
        ctx={ctx}
        route="localization/loc/systemmessage"
        title="Error and system messages"
        noun="message"
        sort="messageKey"
        search
        query={{ localeCode: code }}
        defaults={{ localeCode: code }}
        canWrite={isAdminOf(ctx) || ctx.me.permissions.includes("loc.write")}
        fields={[
          { key: "messageKey", label: "Key", required: true, createOnly: true },
          { key: "localeCode", label: "Language", form: false },
          { key: "messageText", label: "Text", kind: "textarea", required: true },
        ]}
      />
    </>
  );
};

const MoneyTab: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const currencies = useLoad(() => api<CurrencyInfo[]>("GET", "/api/localization/currencies"), []);
  const [conv, setConv] = useState({ from: ctx.me.currencyCode ?? "NGN", to: "USD", amount: "1000", date: "" });
  const [res, setRes] = useState<{ from: string; to: string; amount: number; converted: number; date: string } | null>(null);
  const [rates, setRates] = useState("");
  const [rdate, setRdate] = useState(() => iso(new Date()));
  const [rsrc, setRsrc] = useState("manual");
  const [reloadKey, setReloadKey] = useState(0);
  const codes = (currencies.data ?? []).map((c) => c.currencyCode);
  const canWrite = isAdminOf(ctx) || ctx.me.permissions.includes("loc.write");
  const parsed = rates
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const m = /^([A-Za-z]{3})\s*(?:->|→|to|,|\s)\s*([A-Za-z]{3})\s*(?:=|,|\s)\s*([0-9]*\.?[0-9]+)$/.exec(l);
      return m ? { from: (m[1] ?? "").toUpperCase(), to: (m[2] ?? "").toUpperCase(), rate: Number(m[3]) } : null;
    });
  const bad = parsed.some((p) => p === null);
  return (
    <>
      <div className={tp.card}>
        <h3 className={tp.cardTitle}>Convert an amount</h3>
        <div className={tp.formLine}>
          <Field label="Amount">
            <Input type="number" value={conv.amount} onChange={(_, d) => setConv({ ...conv, amount: d.value })} />
          </Field>
          <Field label="From">
            <Select value={conv.from} onChange={(_, d) => setConv({ ...conv, from: d.value })}>
              {[...new Set([conv.from, ...codes])].map((c) => (
                <option key={c}>{c}</option>
              ))}
            </Select>
          </Field>
          <Field label="To">
            <Select value={conv.to} onChange={(_, d) => setConv({ ...conv, to: d.value })}>
              {[...new Set([conv.to, ...codes])].map((c) => (
                <option key={c}>{c}</option>
              ))}
            </Select>
          </Field>
          <Field label="On (optional)">
            <Input type="date" value={conv.date} onChange={(_, d) => setConv({ ...conv, date: d.value })} />
          </Field>
          <Button
            className={styles.primary}
            appearance="primary"
            disabled={!(Number(conv.amount) >= 0)}
            onClick={() => void act(async () => setRes(await api("GET", "/api/localization/convert", undefined, { from: conv.from, to: conv.to, amount: conv.amount, ...(conv.date ? { date: conv.date } : {}) })))}
          >
            Convert
          </Button>
        </div>
        {res && (
          <div className={tp.tiles}>
            <Tile n={`${res.amount.toLocaleString()} ${res.from}`} label="Amount" />
            <Tile n={`${res.converted.toLocaleString()} ${res.to}`} label={`On ${fmtDate(res.date)}`} />
          </div>
        )}
      </div>
      <EntityTable
        ctx={ctx}
        route="localization/loc/currency"
        title="Currencies"
        noun="currency"
        sort="currencyCode"
        search
        canWrite={canWrite}
        fields={[
          { key: "currencyCode", label: "Code", required: true, placeholder: "NGN", createOnly: true },
          { key: "name", label: "Name", required: true },
          { key: "symbol", label: "Symbol" },
          { key: "decimalDigits", label: "Decimals", kind: "number", required: true },
          { key: "isActive", label: "Available", kind: "bool" },
        ]}
      />
      <div className={tp.card}>
        <h3 className={tp.cardTitle}>Load exchange rates</h3>
        <p className={tp.text}>One rate per line, for example <code>USD to NGN 1500</code> or <code>EUR,NGN,1650</code>.</p>
        <Textarea className={ad.mono} rows={4} value={rates} onChange={(_, d) => setRates(d.value)} aria-label="Exchange rates" />
        <div className={tp.formLine}>
          <Field label="Effective on">
            <Input type="date" value={rdate} onChange={(_, d) => setRdate(d.value)} />
          </Field>
          <Field label="Source">
            <Input value={rsrc} onChange={(_, d) => setRsrc(d.value)} />
          </Field>
          <Button
            className={styles.primary}
            appearance="primary"
            disabled={!canWrite || parsed.length === 0 || bad}
            onClick={async () => {
              const body = parsed.filter((p) => p !== null).map((p) => ({ from: p?.from, to: p?.to, rate: p?.rate, on: rdate, source: rsrc.trim() || null }));
              if (await act(() => api("POST", "/api/localization/rates", body), `${body.length} rates loaded.`)) {
                setRates("");
                setReloadKey((n) => n + 1);
              }
            }}
          >
            Load rates
          </Button>
        </div>
        {bad && <div className={ad.note}>A line isn’t in the form “FROM TO rate”. Fix it before loading.</div>}
      </div>
      <EntityTable
        key={reloadKey}
        ctx={ctx}
        route="localization/loc/exchangerate"
        title="Exchange rates"
        noun="rate"
        sort="-effectiveDate"
        search
        canWrite={canWrite}
        fields={[
          { key: "fromCurrency", label: "From", required: true, placeholder: "USD", createOnly: true },
          { key: "toCurrency", label: "To", required: true, placeholder: "NGN", createOnly: true },
          { key: "rate", label: "Rate", kind: "number", required: true },
          { key: "effectiveDate", label: "Effective", kind: "date", required: true, createOnly: true },
          { key: "source", label: "Source" },
        ]}
      />
    </>
  );
};

const TranslationsTab: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const locales = useLoad(() => api<LocaleInfo[]>("GET", "/api/localization/locales"), []);
  const [loc, setLoc] = useState("");
  const code = loc || locales.data?.[0]?.localeCode || "en";
  const cov = useLoad(() => api<{ entityName: string; translated: number }[]>("GET", "/api/localization/translations/coverage", undefined, { locale: code }), [code]);
  const options: [string, string][] = (locales.data ?? []).map((x) => [x.localeCode, `${x.name} (${x.localeCode})`]);
  const total = (cov.data ?? []).reduce((a, b) => a + num(b.translated), 0);
  return (
    <>
      <div className={tp.card}>
        <div className={tp.rowBetween}>
          <h3 className={tp.cardTitle}>Translation coverage</h3>
          <Select aria-label="Language" value={code} onChange={(_, d) => setLoc(d.value)}>
            {options.length === 0 && <option value={code}>{code}</option>}
            {options.map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </Select>
        </div>
        <Wait loading={cov.loading && !cov.data} error={cov.error} empty={!cov.loading && (cov.data ?? []).length === 0 && "Nothing is translated into this language yet"} onRetry={cov.reload} />
        {(cov.data ?? []).length > 0 && (
          <>
            <div className={tp.tiles}>
              <Tile n={total.toLocaleString()} label="Translated texts" />
              <Tile n={(cov.data ?? []).length} label="Kinds of content" />
            </div>
            <BarChart s={{ labels: (cov.data ?? []).map((c) => c.entityName), values: (cov.data ?? []).map((c) => num(c.translated)), name: "translated texts" }} />
          </>
        )}
      </div>
      <EntityTable
        ctx={ctx}
        route="localization/loc/translation"
        title={`Translated content (${code})`}
        noun="translation"
        sort="entityName"
        search
        query={{ localeCode: code }}
        defaults={{ localeCode: code }}
        canWrite={isAdminOf(ctx) || ctx.me.permissions.includes("loc.write")}
        fields={[
          { key: "entityName", label: "Kind", required: true, placeholder: "prj.Project", createOnly: true },
          { key: "entityKey", label: "Item id", required: true, createOnly: true },
          { key: "fieldName", label: "Field", required: true, placeholder: "Name", createOnly: true },
          { key: "localeCode", label: "Language", form: false },
          { key: "textValue", label: "Text", kind: "textarea", required: true },
        ]}
      />
    </>
  );
};

/* =================================================================== *
 *  Devices and offline sync
 * =================================================================== */
interface SyncDevice {
  deviceId: string;
  name?: string | null;
  platform: string;
  appVersion?: string | null;
  lastSeenAt?: string | null;
  lastCursor?: number | null;
}
interface Manifest {
  serverTime: string;
  changeTracking: boolean;
  maxBatch: number;
  tables: { name: string; writable: boolean; scope: string }[];
  actions: string[];
  notes?: string;
}
interface BootPage {
  table: string;
  rows: unknown[];
  hasMore: boolean;
  nextAfter: number;
  cursor: number | null;
}
interface PullOut {
  reset: boolean;
  cursor: number;
  hasMore: boolean;
  serverTime: string;
  resyncWorkspaces: number[];
  changes: { table: string; op: string; key: number; version: number }[];
}

const thisDevice = (): string => {
  try {
    let id = localStorage.getItem("tm-device-id");
    if (!id) {
      id = `web-${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
      localStorage.setItem("tm-device-id", id);
    }
    return id;
  } catch {
    return "web-session-device";
  }
};

export const DevicesPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const [tab, setTab] = useState("sync");
  return (
    <>
      <Head icon={<PhoneLaptop20Regular />} title="Devices & offline" sub="Phones and browsers that sync, push notification devices, and offline readiness.">
        <span className={styles.muted}>This browser: {thisDevice()}</span>
      </Head>
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          ["sync", "Sync devices"],
          ["push", "Push devices"],
          ["offline", "Offline check"],
        ]}
      />
      <Page>
        {tab === "sync" && <SyncDevicesTab ctx={ctx} />}
        {tab === "push" && <PushDevicesTab ctx={ctx} />}
        {tab === "offline" && <OfflineTab ctx={ctx} />}
      </Page>
    </>
  );
};

const SyncDevicesTab: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", platform: "WEB", version: "" });
  const [sure, setSure] = useState<string | null>(null);
  const l = useLoad(() => api<SyncDevice[]>("GET", "/api/sync/devices"), []);
  const me = thisDevice();
  return (
    <>
      <div className={tp.card}>
        <div className={tp.rowBetween}>
          <h3 className={tp.cardTitle}>Devices that sync</h3>
          <Button
            className={styles.primary}
            appearance="primary"
            icon={<Add20Regular />}
            onClick={() => {
              setF({ name: navigator.platform || "This browser", platform: "WEB", version: "" });
              setOpen(true);
            }}
          >
            Register this browser
          </Button>
        </div>
        <Wait loading={l.loading && !l.data} error={l.error} empty={!l.loading && (l.data ?? []).length === 0 && "No devices registered yet"} onRetry={l.reload} />
        {(l.data ?? []).map((d) => (
          <div key={d.deviceId} className={tp.item}>
            <div className={tp.itemMain}>
              <span className={tp.itemTitle}>
                {d.name || d.deviceId} <Chip text={d.platform} /> {d.deviceId === me && <Chip text="This browser" />}
              </span>
              <span className={tp.itemMeta}>
                {d.appVersion ? `v${d.appVersion} · ` : ""}last seen {fmtStamp(d.lastSeenAt)} · position {d.lastCursor ?? "—"}
              </span>
            </div>
            {sure === d.deviceId ? (
              <>
                <Button
                  size="small"
                  onClick={async () => {
                    setSure(null);
                    if (await act(() => api("DELETE", `/api/sync/devices/${encodeURIComponent(d.deviceId)}`), "Device removed.")) l.reload();
                  }}
                >
                  Remove it
                </Button>
                <Button size="small" appearance="subtle" onClick={() => setSure(null)}>
                  Keep
                </Button>
              </>
            ) : (
              <Button size="small" appearance="subtle" icon={<Delete20Regular />} aria-label={`Remove ${d.name || d.deviceId}`} onClick={() => setSure(d.deviceId)} />
            )}
          </div>
        ))}
        <p className={tp.text}>Removing a device (for example a lost phone) also deletes its stored offline change history. It can register again later.</p>
      </div>
      <FormDialog
        open={open}
        title="Register this browser"
        action="Register"
        onClose={() => setOpen(false)}
        onSubmit={async () => {
          if (await act(() => api("POST", "/api/sync/devices", { deviceId: me, name: f.name.trim() || null, platform: f.platform, appVersion: f.version.trim() || null }), "Registered.")) {
            setOpen(false);
            l.reload();
          }
        }}
      >
        <Field label="Name">
          <Input value={f.name} onChange={(_, d) => setF({ ...f, name: d.value })} />
        </Field>
        <Field label="Kind">
          <Select value={f.platform} onChange={(_, d) => setF({ ...f, platform: d.value })}>
            {["WEB", "DESKTOP", "ANDROID", "IOS"].map((p) => (
              <option key={p} value={p}>
                {nice(p)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="App version (optional)">
          <Input value={f.version} onChange={(_, d) => setF({ ...f, version: d.value })} />
        </Field>
        <div className={styles.muted}>Device id: {me}</div>
      </FormDialog>
      <EntityTable
        ctx={ctx}
        route="sync/sync/mutation"
        title="Offline changes received"
        noun="change"
        sort="-id"
        canWrite={false}
        canCreate={false}
        canDelete={false}
        emptyText="No offline changes have been sent yet"
        fields={[
          { key: "deviceId", label: "Device" },
          { key: "entity", label: "What" },
          { key: "operation", label: "Action" },
          { key: "entityKey", label: "Item" },
          { key: "status", label: "Result" },
          { key: "createdAt", label: "When", kind: "datetime" },
        ]}
      />
    </>
  );
};

const PushDevicesTab: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ token: "", platform: "WEB", name: "" });
  const [key, setKey] = useState(0);
  return (
    <>
      <EntityTable
        key={key}
        ctx={ctx}
        route="notifications/ntf/pushdevice"
        title="Devices that get push notifications"
        noun="device"
        sort="-lastSeenAt"
        canWrite={false}
        canCreate={false}
        canDelete={false}
        emptyText="No push devices registered"
        fields={[
          { key: "deviceName", label: "Name" },
          { key: "platform", label: "Kind" },
          { key: "lastSeenAt", label: "Last seen", kind: "datetime" },
          { key: "token", label: "Token" },
        ]}
        toolbar={
          <Button className={styles.primary} appearance="primary" icon={<Add20Regular />} onClick={() => setOpen(true)}>
            Register device
          </Button>
        }
        rowActions={(r, reload) => (
          <Button
            size="small"
            appearance="subtle"
            icon={<Delete20Regular />}
            aria-label={`Unregister ${str(r.deviceName) || "device"}`}
            onClick={async () => {
              if (await act(() => api("DELETE", "/api/notifications/devices", undefined, { token: str(r.token) }), "Device unregistered.")) reload();
            }}
          />
        )}
      />
      <FormDialog
        open={open}
        title="Register a push device"
        action="Register"
        disabled={!f.token.trim()}
        onClose={() => setOpen(false)}
        onSubmit={async () => {
          if (await act(() => api("POST", "/api/notifications/devices", { token: f.token.trim(), platform: f.platform, deviceName: f.name.trim() || null }), "Device registered.")) {
            setOpen(false);
            setF({ token: "", platform: "WEB", name: "" });
            setKey((n) => n + 1);
          }
        }}
      >
        <Field label="Push token" required hint="The token your phone app or browser gets from its push service.">
          <Textarea rows={3} value={f.token} onChange={(_, d) => setF({ ...f, token: d.value })} className={ad.mono} />
        </Field>
        <div className={styles.formRow}>
          <Field label="Kind" className={styles.grow}>
            <Select value={f.platform} onChange={(_, d) => setF({ ...f, platform: d.value })}>
              {["WEB", "ANDROID", "IOS"].map((p) => (
                <option key={p} value={p}>
                  {nice(p)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Name" className={styles.grow}>
            <Input value={f.name} onChange={(_, d) => setF({ ...f, name: d.value })} />
          </Field>
        </div>
      </FormDialog>
    </>
  );
};

interface CheckRow {
  table: string;
  writable: boolean;
  rows: number;
  more: boolean;
  error: string;
}

const OfflineTab: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const man = useLoad(() => api<Manifest>("GET", "/api/sync/manifest"), []);
  const [checks, setChecks] = useState<CheckRow[] | null>(null);
  const [running, setRunning] = useState(false);
  const [cursor, setCursor] = useState<number | null>(null);
  const [pull, setPull] = useState<PullOut | null>(null);
  const [pulling, setPulling] = useState(false);
  const [done, setDone] = useState(0);
  const m = man.data;
  const device = thisDevice();

  const run = async () => {
    if (!m) return;
    setRunning(true);
    setDone(0);
    setPull(null);
    const out: CheckRow[] = [];
    let first: number | null = null;
    for (const t of m.tables) {
      try {
        const p = await api<BootPage>("GET", "/api/sync/bootstrap", undefined, { table: t.name, limit: 50, deviceId: device, ...(ctx.workspaceId ? { workspaceId: ctx.workspaceId } : {}) });
        if (first === null && p.cursor !== null && p.cursor !== undefined) first = p.cursor;
        out.push({ table: t.name, writable: t.writable, rows: p.rows.length, more: p.hasMore, error: "" });
      } catch (e) {
        out.push({ table: t.name, writable: t.writable, rows: 0, more: false, error: msgOf(e) });
      }
      setDone(out.length);
      setChecks([...out]);
    }
    setCursor(first);
    setRunning(false);
  };

  const doPull = async () => {
    if (cursor === null) return;
    setPulling(true);
    await act(async () => {
      const r = await api<PullOut>("GET", "/api/sync/pull", undefined, { cursor, limit: 100, deviceId: device });
      setPull(r);
      if (!r.reset) setCursor(r.cursor);
    });
    setPulling(false);
  };

  return (
    <>
      <div className={tp.card}>
        <div className={tp.rowBetween}>
          <h3 className={tp.cardTitle}>What the server offers</h3>
          <Button icon={<ArrowSync20Regular />} onClick={man.reload}>
            Refresh
          </Button>
        </div>
        <Wait loading={man.loading && !m} error={man.error} onRetry={man.reload} />
        {m && (
          <>
            <div className={tp.tiles}>
              <Tile n={m.changeTracking ? "Ready" : "Off"} label="Change tracking" />
              <Tile n={m.tables.length} label="Tables to download" />
              <Tile n={m.maxBatch} label="Changes per batch" />
              <Tile n={fmtStamp(m.serverTime)} label="Server time" />
            </div>
            {!m.changeTracking && <div className={ad.note}>Change tracking is switched off in the database, so devices can’t download or pull yet. Ask whoever runs the database to enable it.</div>}
            {m.notes && <p className={tp.text}>{m.notes}</p>}
            <div className={tp.block}>
              <div className={tp.blockTitle}>Actions a device can run offline and replay later</div>
              <div className={ad.tags}>
                {m.actions.map((a) => (
                  <code key={a} className={ad.tag}>
                    {a}
                  </code>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
      <div className={tp.card}>
        <div className={tp.rowBetween}>
          <h3 className={tp.cardTitle}>Test a download on this browser</h3>
          <Button className={styles.primary} appearance="primary" disabled={!m || !m.changeTracking || running} onClick={() => void run()}>
            {running ? `Checking… ${done}/${m?.tables.length ?? 0}` : "Run download check"}
          </Button>
        </div>
        <p className={tp.text}>Downloads the first page (50 rows) of every table for the current workspace, the same way a phone does the first time. Nothing is stored here.</p>
        {checks && (
          <div className={tp.scroll}>
            <table className={tp.tbl}>
              <thead>
                <tr>
                  <th>Table</th>
                  <th>Can change offline</th>
                  <th>Rows (first page)</th>
                  <th>More</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {checks.map((c) => (
                  <tr key={c.table}>
                    <td className={tp.strong}>{c.table}</td>
                    <td>{c.writable ? "Yes" : "Read only"}</td>
                    <td>{c.rows}</td>
                    <td>{c.more ? "Yes" : "No"}</td>
                    <td>{c.error ? <span className={tp.late}>{c.error}</span> : <Chip text="OK" />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className={tp.formLine}>
          <Button disabled={cursor === null || pulling} onClick={() => void doPull()}>
            {pulling ? "Checking…" : "Check for changes"}
          </Button>
          <span className={styles.muted}>{cursor === null ? "Run the download check first to get a starting position." : `Position ${cursor}`}</span>
        </div>
        {pull && (
          <div className={tp.block}>
            <div className={tp.blockTitle}>
              {pull.reset ? "The position is too old: download everything again." : pull.changes.length === 0 ? "Nothing has changed since the download." : `${pull.changes.length} changes${pull.hasMore ? " (more waiting)" : ""}`}
            </div>
            {pull.resyncWorkspaces.length > 0 && <div className={ad.note}>You were added to workspace {pull.resyncWorkspaces.join(", ")}: download it again.</div>}
            {pull.changes.length > 0 && (
              <div className={tp.scroll}>
                <table className={tp.tbl}>
                  <tbody>
                    {pull.changes.slice(0, 50).map((c, i) => (
                      <tr key={i}>
                        <td>{c.table}</td>
                        <td>
                          <Chip text={c.op} />
                        </td>
                        <td>#{c.key}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

/* =================================================================== *
 *  Connectors: Slack / Teams / generic, inbound e-mail, redelivery
 * =================================================================== */
interface HookLite {
  id: number;
  url: string;
}
interface DeliveryLite {
  id: number;
  eventType: string;
  statusCode?: number | null;
  attempts: number;
  deliveredAt?: string | null;
}

export const ConnectorsPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const [tab, setTab] = useState("conn");
  const admin = isAdminOf(ctx);
  return (
    <>
      <Head icon={<PlugConnected20Regular />} title="Connectors" sub="Chat connectors, inbound e-mail to projects, and webhook delivery history.">
        <span className={styles.muted}>{admin ? "You can manage connectors" : "Only admins can change these"}</span>
      </Head>
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          ["conn", "Chat connectors"],
          ["mail", "Inbound e-mail"],
          ["deliveries", "Webhook deliveries"],
        ]}
      />
      <Page>
        {tab === "conn" && <ConnectorTab ctx={ctx} admin={admin} />}
        {tab === "mail" && (
          <EntityTable
            ctx={ctx}
            route="integration/int/inboundemailaddress"
            title="Project mailboxes"
            noun="mailbox"
            query={{ workspaceId: ctx.workspaceId }}
            defaults={{ workspaceId: ctx.workspaceId }}
            canWrite={admin}
            emptyText="No mailboxes. Add one and e-mails sent to it become tasks in the project."
            fields={[
              { key: "address", label: "Address", required: true, placeholder: "support@tasks.cilpron.com" },
              { key: "projectId", label: "Creates tasks in", kind: "project", required: true },
              { key: "defaultAssigneeId", label: "Assign to", kind: "user" },
            ]}
          />
        )}
        {tab === "deliveries" && <DeliveriesTab ctx={ctx} admin={admin} />}
      </Page>
    </>
  );
};

const ConnectorTab: React.FC<{ ctx: PageCtx; admin: boolean }> = ({ ctx, admin }) => {
  const act = useAct(ctx.notify);
  const [testing, setTesting] = useState<number | null>(null);
  return (
    <EntityTable
      ctx={ctx}
      route="integration/int/connector"
      title="Connectors"
      noun="connector"
      sort="name"
      search
      query={{ workspaceId: ctx.workspaceId }}
      defaults={{ workspaceId: ctx.workspaceId }}
      canWrite={admin}
      emptyText="No connectors yet"
      fields={[
        { key: "name", label: "Name", required: true },
        { key: "provider", label: "Service", kind: "select", required: true, options: [["SLACK", "Slack"], ["TEAMS", "Microsoft Teams"], ["GENERIC", "Generic webhook"]] },
        { key: "status", label: "Status", kind: "select", required: true, options: [["ACTIVE", "Active"], ["PAUSED", "Paused"], ["ERROR", "Error"]] },
        { key: "configJson", label: "Settings", kind: "json", placeholder: '{"url": "https://hooks.slack.com/services/…"}', hint: "The incoming-webhook address and options for this service, as JSON.", list: false },
        { key: "lastSyncAt", label: "Last used", kind: "datetime", form: false },
      ]}
      rowActions={(r, reload) => (
        <Button
          size="small"
          disabled={!admin || testing === r.id}
          onClick={async () => {
            setTesting(r.id);
            await act(async () => {
              const out = await api<{ status: string }>("POST", `/api/integrations/connectors/${r.id}/test`);
              ctx.notify(out.status === "ACTIVE" ? "success" : "info", `Test sent. Status: ${nice(out.status || "unknown")}.`);
            });
            setTesting(null);
            reload();
          }}
        >
          {testing === r.id ? "Sending…" : "Send test"}
        </Button>
      )}
    />
  );
};

const DeliveriesTab: React.FC<{ ctx: PageCtx; admin: boolean }> = ({ ctx, admin }) => {
  const act = useAct(ctx.notify);
  const [sel, setSel] = useState<number | null>(null);
  const [limit, setLimit] = useState("50");
  const [busy, setBusy] = useState<number | null>(null);
  const hooks = useLoad(async () => (await crud.list<HookLite>(R.webhook, { workspaceId: ctx.workspaceId })).items, [ctx.workspaceId]);
  const list = hooks.data ?? [];
  const cur = sel ?? list[0]?.id ?? null;
  const log = useLoad(async () => (cur === null ? [] : asList<DeliveryLite>(await api("GET", `/api/integrations/webhooks/${cur}/deliveries`, undefined, { limit: Number(limit) || 50 }))), [cur, limit]);
  const rows = log.data ?? [];
  const failed = rows.filter((d) => !d.statusCode || d.statusCode >= 300).length;
  return (
    <div className={tp.card}>
      <div className={tp.rowBetween}>
        <h3 className={tp.cardTitle}>Delivery history</h3>
        <div className={tp.actions}>
          <Select aria-label="Webhook" value={cur === null ? "" : String(cur)} onChange={(_, d) => setSel(Number(d.value))}>
            {list.length === 0 && <option value="">No webhooks</option>}
            {list.map((h) => (
              <option key={h.id} value={String(h.id)}>
                {h.url}
              </option>
            ))}
          </Select>
          <Select aria-label="How many" value={limit} onChange={(_, d) => setLimit(d.value)}>
            {["25", "50", "100", "200"].map((n) => (
              <option key={n} value={n}>
                Last {n}
              </option>
            ))}
          </Select>
          <Button icon={<ArrowSync20Regular />} onClick={log.reload}>
            Refresh
          </Button>
        </div>
      </div>
      <Wait loading={(hooks.loading && !hooks.data) || (log.loading && !log.data)} error={hooks.error || log.error} empty={!hooks.loading && list.length === 0 ? "Create a webhook under Integrations first" : !log.loading && rows.length === 0 && "Nothing was sent to this webhook yet"} onRetry={log.reload} />
      {rows.length > 0 && (
        <>
          <div className={tp.tiles}>
            <Tile n={rows.length} label="Shown" />
            <Tile n={rows.length - failed} label="Delivered" />
            <Tile n={failed} label="Failed" />
          </div>
          <div className={tp.scroll}>
            <table className={tp.tbl}>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Event</th>
                  <th>Result</th>
                  <th>Tries</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.id}>
                    <td>{fmtStamp(d.deliveredAt)}</td>
                    <td className={tp.strong}>{d.eventType}</td>
                    <td>
                      <Chip text={d.statusCode && d.statusCode < 300 ? "OK" : `Error ${d.statusCode ?? ""}`.trim()} />
                    </td>
                    <td>{d.attempts}</td>
                    <td>
                      <Button
                        size="small"
                        disabled={!admin || busy === d.id}
                        onClick={async () => {
                          setBusy(d.id);
                          await act(async () => {
                            const r = await api<{ status: number; attempts: number }>("POST", `/api/integrations/deliveries/${d.id}/redeliver`);
                            ctx.notify(r.status < 300 ? "success" : "error", `Sent again. The receiver answered ${r.status}.`);
                          });
                          setBusy(null);
                          log.reload();
                        }}
                      >
                        {busy === d.id ? "Sending…" : "Send again"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

/* keep the unused-import check honest for helpers only some builds use */
void useEffect;